/**
 * OsirisBrain ERP — Business Type Configuration
 *
 * This is the master config that controls what the ENTIRE app shows
 * based on the tenant's selected business type.
 *
 * When a user selects a business type in Settings, this config drives:
 * - Sidebar menu items
 * - Product form fields
 * - Employee roles available
 * - Labels/terminology throughout the app
 * - Which modules are active
 * - POS behavior
 */

// ── Types ──────────────────────────────────────────────────────────────

export interface BusinessLabels {
  product: string; // What to call items being sold
  productPlural: string;
  customer: string; // What to call people you serve
  customerPlural: string;
  inventory: string; // What to call stock/catalog
  order: string; // What to call a transaction/service request
  orderPlural: string;
  supplier: string; // What to call vendors
  supplierPlural: string;
  category: string; // What to call product categories
  categoryPlural: string;
}

export interface BusinessModules {
  pos: boolean; // Point of Sale
  kds: boolean; // Kitchen Display System
  delivery: boolean; // Delivery tracking
  reservations: boolean; // Table/room reservations
  appointments: boolean; // Appointment scheduling
  prescriptions: boolean; // Medical prescriptions
  vehicleIntake: boolean; // Vehicle check-in (workshop)
  roomManagement: boolean; // Hotel room management
  patientRecords: boolean; // Medical/health records
  projectManagement: boolean; // Construction/project tracking
  fleetManagement: boolean; // Vehicle fleet (rental)
  membership: boolean; // Membership management (gym)
  classSchedule: boolean; // Class scheduling (education/gym)
  tableManagement: boolean; // Restaurant table map
  loyalty: boolean; // Loyalty/points program
  ecommerce: boolean; // Online store
  serviceOrders: boolean; // Service work orders
  warranties: boolean; // Product warranties
  installations: boolean; // Installation tracking
  crm: boolean; // CRM / sales pipeline
  promotions: boolean; // Promotions/discounts
  commissions: boolean; // Sales commissions
  multiBranch: boolean; // Multi-location
  multiCurrency: boolean; // Multiple currencies
  serialNumbers: boolean; // Serial number tracking
  lotTracking: boolean; // Lot/batch tracking
  expirationTracking: boolean; // Expiration date tracking
  recipeManagement: boolean; // Recipe/ingredients (food)
  weightPricing: boolean; // Weight-based pricing
  tipManagement: boolean; // Tip handling
  splitBill: boolean; // Split bill feature
}

export interface EmployeeType {
  code: string;
  label: string;
  desc: string;
}

export interface SidebarItem {
  path: string;
  label: string;
  icon: string;
  badge?: string;
}

export interface SidebarSection {
  key: string;
  title: string;
  icon: string;
  items: SidebarItem[];
  defaultOpen?: boolean;
}

export interface ProductField {
  key: string;
  label: string;
  type:
    | "text"
    | "number"
    | "select"
    | "boolean"
    | "date"
    | "multi_select"
    | "image";
  options?: string[];
  required?: boolean;
  showInPOS?: boolean;
  showInInventory?: boolean;
  isVariant?: boolean;
  sortOrder: number;
}

export interface POSConfig {
  layout: "grid" | "list" | "counter";
  showCategories: boolean;
  showSearch: boolean;
  showImages: boolean;
  showPrice: boolean;
  showStock: boolean;
  quickPay: boolean;
  splitBill: boolean;
  tipPrompt: boolean;
  customFields: string[]; // product field keys to show
}

export interface BusinessTypeConfig {
  code: string;
  name: string;
  icon: string;
  labels: BusinessLabels;
  modules: BusinessModules;
  employeeTypes: EmployeeType[];
  sidebar: SidebarSection[];
  productFields: ProductField[];
  pos: POSConfig;
}

// ── Helper: Common Sidebar Sections ────────────────────────────────────

const salesSection = (labels: BusinessLabels): SidebarSection => ({
  key: "sales",
  title: `${labels.orderPlural} y Cobro`,
  icon: "DollarSign",
  defaultOpen: true,
  items: [
    {
      path: "/sales/transactions",
      label: labels.orderPlural,
      icon: "ClipboardList",
    },
    { path: "/pos/linear", label: "Facturación", icon: "FileText" },
    { path: "/sales/products", label: labels.productPlural, icon: "Tag" },
    { path: "/pos/visual", label: "Terminal POS", icon: "Grid" },
    { path: "/hub/promotions", label: "Promociones", icon: "Percent" },
    { path: "/hub/print-invoice", label: "Imprimir Facturas", icon: "Printer" },
  ],
});

const customerSection = (labels: BusinessLabels): SidebarSection => ({
  key: "customers",
  title: `Centro de ${labels.customerPlural}`,
  icon: "Users",
  items: [
    { path: "/hub/customers", label: labels.customerPlural, icon: "Users" },
    {
      path: "/hub/service-orders",
      label: `Órdenes de ${labels.order}`,
      icon: "Wrench",
    },
    { path: "/hub/crm", label: "CRM & Lealtad", icon: "Star" },
    { path: "/hub/credit-accounts", label: "CxC / CxP", icon: "CreditCard" },
  ],
});

const inventorySection = (labels: BusinessLabels): SidebarSection => ({
  key: "inventory",
  title: labels.inventory,
  icon: "Package",
  items: [
    {
      path: "/inventory/overview",
      label: `Resumen ${labels.inventory}`,
      icon: "Package",
    },
    { path: "/inventory/advanced", label: "Avanzado", icon: "Package" },
  ],
});

const purchasingSection = (labels: BusinessLabels): SidebarSection => ({
  key: "purchasing",
  title: "Gastos y Compras",
  icon: "Wallet",
  items: [
    { path: "/hub/expenses", label: "Gastos", icon: "Receipt" },
    { path: "/hub/suppliers", label: labels.supplierPlural, icon: "Truck" },
    {
      path: "/hub/purchase-orders",
      label: "Órdenes de Compra",
      icon: "ShoppingCart",
    },
    {
      path: "/hub/accounts-payable",
      label: "Cuentas por Pagar",
      icon: "FileText",
    },
  ],
});

const accountingSection: SidebarSection = {
  key: "accounting",
  title: "Contabilidad",
  icon: "BookOpen",
  items: [
    { path: "/hub/accounting", label: "Plan de Cuentas", icon: "BookOpen" },
    { path: "/hub/ledger", label: "Libro Mayor", icon: "FileText" },
    { path: "/hub/fixed-assets", label: "Activos Fijos", icon: "Building2" },
    { path: "/hub/period-closing", label: "Cierre Contable", icon: "Lock" },
    { path: "/ai/reconciliation", label: "Conciliar Bancos", icon: "Landmark" },
    { path: "/hub/reports", label: "Estados Financieros", icon: "BarChart3" },
  ],
};

const taxSection: SidebarSection = {
  key: "taxes",
  title: "Impuestos",
  icon: "Calculator",
  items: [{ path: "/hub/taxes", label: "606 / 607 & e-CF", icon: "FileText" }],
};

const hrSection: SidebarSection = {
  key: "hr",
  title: "Recursos Humanos",
  icon: "Users",
  items: [
    { path: "/hub/hr", label: "Empleados & TSS", icon: "Users" },
    {
      path: "/hub/advanced-payroll",
      label: "Nómina Avanzada",
      icon: "Calculator",
    },
    { path: "/hub/commissions", label: "Comisiones", icon: "Coins" },
  ],
};

const operationsSection: SidebarSection = {
  key: "operations",
  title: "Operaciones",
  icon: "Clock",
  items: [
    { path: "/hub/cash-shifts", label: "Turnos y Caja", icon: "Clock" },
    { path: "/hub/delivery", label: "Delivery", icon: "Truck" },
    { path: "/hub/kds", label: "Cocina (KDS)", icon: "UtensilsCrossed" },
    { path: "/hub/reservations", label: "Reservaciones", icon: "CalendarDays" },
  ],
};

const postSaleSection: SidebarSection = {
  key: "postsale",
  title: "Post-Venta",
  icon: "ShieldCheck",
  items: [
    { path: "/hub/warranties", label: "Garantías", icon: "ShieldCheck" },
    { path: "/hub/installations", label: "Instalaciones", icon: "Wrench" },
  ],
};

const biSection: SidebarSection = {
  key: "intelligence",
  title: "Inteligencia",
  icon: "TrendingUp",
  items: [
    { path: "/hub/bi-analytics", label: "BI / Analytics", icon: "BarChart3" },
    { path: "/hub/multi-currency", label: "Multi-Moneda", icon: "Banknote" },
    { path: "/hub/banking", label: "Conciliación Bancaria", icon: "Landmark" },
  ],
};

const ecommerceSection: SidebarSection = {
  key: "ecommerce",
  title: "Ventas Online",
  icon: "Globe",
  items: [
    { path: "/hub/ecommerce", label: "E-Commerce", icon: "ShoppingCart" },
    { path: "/hub/webchat", label: "Webchat IA", icon: "MessageCircle" },
  ],
};

// ── Common employee types ──────────────────────────────────────────────

const commonEmployees: EmployeeType[] = [
  {
    code: "manager",
    label: "Gerente",
    desc: "Gerente general o de operaciones",
  },
  { code: "cashier", label: "Cajero(a)", desc: "Manejo de caja y facturación" },
  {
    code: "admin",
    label: "Administrativo",
    desc: "Tareas administrativas generales",
  },
  {
    code: "cleaner",
    label: "Limpieza",
    desc: "Personal de limpieza y mantenimiento",
  },
  { code: "security", label: "Seguridad", desc: "Personal de seguridad" },
  { code: "delivery", label: "Delivery", desc: "Repartidor / motorista" },
];

// ── Full Config for All 67 Business Types ──────────────────────────────

export const BUSINESS_TYPE_CONFIGS: Record<string, BusinessTypeConfig> = {
  // ═══════════════════════════════════════════════════════════════
  // FOOD & BEVERAGE
  // ═══════════════════════════════════════════════════════════════

  restaurant: {
    code: "restaurant",
    name: "Restaurante",
    icon: "UtensilsCrossed",
    labels: {
      product: "Plato",
      productPlural: "Menú",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Orden",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: true,
      delivery: true,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: true,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: true,
      weightPricing: false,
      tipManagement: true,
      splitBill: true,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "waiter",
        label: "Mesero(a)",
        desc: "Atención de mesas y pedidos",
      },
      { code: "cook", label: "Cocinero(a)", desc: "Preparación de alimentos" },
      { code: "chef", label: "Chef", desc: "Chef ejecutivo o de cocina" },
      {
        code: "bartender",
        label: "Bartender",
        desc: "Preparación de bebidas y coctelería",
      },
      {
        code: "host",
        label: "Host/Hostess",
        desc: "Recepción y asignación de mesas",
      },
      { code: "runner", label: "Runner", desc: "Entrega de comida a mesas" },
      { code: "sommelier", label: "Sommelier", desc: "Experto en vinos" },
    ],
    sidebar: [
      salesSection({
        product: "Plato",
        productPlural: "Menú",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Orden",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      customerSection({
        customer: "Cliente",
        customerPlural: "Clientes",
      } as any),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      operationsSection,
      biSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "spice_level",
        label: "Nivel Picante",
        type: "select",
        options: ["No picante", "Suave", "Medio", "Picante", "Muy picante"],
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "allergens",
        label: "Alérgenos",
        type: "multi_select",
        options: [
          "Gluten",
          "Lácteos",
          "Huevo",
          "Soja",
          "Mariscos",
          "Frutos secos",
          "Ninguno",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "dietary",
        label: "Dieta Especial",
        type: "select",
        options: [
          "Normal",
          "Vegetariano",
          "Vegano",
          "Sin Gluten",
          "Keto",
          "Halal",
        ],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "prep_time",
        label: "Tiempo Prep (min)",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "calories",
        label: "Calorías",
        type: "number",
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "portion",
        label: "Porción",
        type: "select",
        options: ["Individual", "Para 2", "Familiar", "Personal"],
        showInPOS: true,
        sortOrder: 6,
      },
      {
        key: "recipe",
        label: "Receta/Ingredientes",
        type: "text",
        showInPOS: false,
        sortOrder: 7,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: true,
      tipPrompt: true,
      customFields: ["spice_level", "allergens", "dietary", "portion"],
    },
  },

  bar_lounge: {
    code: "bar_lounge",
    name: "Bar / Lounge",
    icon: "Wine",
    labels: {
      product: "Bebida",
      productPlural: "Carta de Bebidas",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Cuenta",
      orderPlural: "Cuentas",
      supplier: "Distribuidor",
      supplierPlural: "Distribuidores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: true,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: true,
      weightPricing: false,
      tipManagement: true,
      splitBill: true,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "bartender",
        label: "Bartender",
        desc: "Preparación de cocteles y bebidas",
      },
      { code: "server", label: "Mesero(a)", desc: "Servicio de mesa" },
      { code: "host", label: "Host/Hostess", desc: "Recepción de clientes" },
      { code: "dj", label: "DJ", desc: "Música y ambiente" },
      { code: "bouncer", label: "Seguridad Puerta", desc: "Control de acceso" },
    ],
    sidebar: [
      salesSection({
        product: "Bebida",
        productPlural: "Carta",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Cuenta",
        orderPlural: "Cuentas",
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      customerSection({
        customer: "Cliente",
        customerPlural: "Clientes",
      } as any),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      operationsSection,
      biSection,
    ],
    productFields: [
      {
        key: "drink_type",
        label: "Tipo de Bebida",
        type: "select",
        options: [
          "Cóctel",
          "Cerveza",
          "Shot",
          "Vino",
          "Sin Alcohol",
          "Licores",
        ],
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "volume",
        label: "Volumen (ml)",
        type: "number",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "abv",
        label: "Graduación %",
        type: "number",
        showInPOS: false,
        sortOrder: 3,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "origin",
        label: "País de Origen",
        type: "text",
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "is_premium",
        label: "Premium",
        type: "boolean",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: true,
      tipPrompt: true,
      customFields: ["drink_type", "volume", "is_premium"],
    },
  },

  bakery: {
    code: "bakery",
    name: "Panadería / Repostería",
    icon: "Croissant",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Pedido",
      orderPlural: "Pedidos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: true,
      recipeManagement: true,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "baker",
        label: "Panadero(a)",
        desc: "Preparación de panes y pasteles",
      },
      {
        code: "pastry_chef",
        label: "Pastelero(a)",
        desc: "Repostería fina y tortas",
      },
      {
        code: "decorator",
        label: "Decorador(a)",
        desc: "Decoración de pasteles",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Pedido",
        orderPlural: "Pedidos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      customerSection({
        customer: "Cliente",
        customerPlural: "Clientes",
      } as any),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      operationsSection,
      biSection,
    ],
    productFields: [
      {
        key: "bread_type",
        label: "Tipo",
        type: "select",
        options: [
          "Pan",
          "Pastel",
          "Galleta",
          "Croissant",
          "Torta",
          "Postre",
          "Otro",
        ],
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "weight",
        label: "Peso (g)",
        type: "number",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "filling",
        label: "Relleno",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "is_fresh_today",
        label: "Hecho Hoy",
        type: "boolean",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "dietary",
        label: "Dieta",
        type: "select",
        options: ["Normal", "Sin Gluten", "Vegano", "Sin Azúcar"],
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "expiration",
        label: "Vence",
        type: "date",
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["bread_type", "weight", "is_fresh_today"],
    },
  },

  food_truck: {
    code: "food_truck",
    name: "Food Truck / Comida Rápida",
    icon: "Truck",
    labels: {
      product: "Producto",
      productPlural: "Menú",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Orden",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: true,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: true,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "cook", label: "Cocinero(a)", desc: "Preparación de comida" },
      {
        code: "order_taker",
        label: "Tomador de Orden",
        desc: "Recibe pedidos de clientes",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Menú",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Orden",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      operationsSection,
    ],
    productFields: [
      {
        key: "dish_type",
        label: "Tipo",
        type: "select",
        options: [
          "Hamburguesa",
          "Taco",
          "Pizza",
          "Pollo",
          "Empanada",
          "Combo",
          "Bebida",
        ],
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "spice",
        label: "Picante",
        type: "select",
        options: ["No", "Suave", "Medio", "Picante"],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "size",
        label: "Tamaño",
        type: "select",
        options: ["Regular", "Grande", "Familiar"],
        showInPOS: true,
        sortOrder: 3,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: false,
      quickPay: true,
      splitBill: false,
      tipPrompt: true,
      customFields: ["dish_type", "spice", "size"],
    },
  },

  catering: {
    code: "catering",
    name: "Catering / Eventos",
    icon: "Calendar",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Evento",
      orderPlural: "Eventos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: false,
      kds: false,
      delivery: true,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: true,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: true,
      multiBranch: false,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: true,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "chef", label: "Chef", desc: "Chef de eventos" },
      { code: "server", label: "Mesero(a)", desc: "Servicio en evento" },
      {
        code: "coordinator",
        label: "Coordinador(a)",
        desc: "Coordinación de eventos",
      },
      { code: "bartender", label: "Bartender", desc: "Barra de bebidas" },
    ],
    sidebar: [
      {
        key: "events",
        title: "Eventos",
        icon: "Calendar",
        defaultOpen: true,
        items: [
          { path: "/sales/transactions", label: "Eventos", icon: "Calendar" },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
          {
            path: "/hub/service-orders",
            label: "Cotizaciones",
            icon: "FileText",
          },
        ],
      },
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo de Servicio",
        type: "select",
        options: [
          "Buffet",
          "Plato Servido",
          "Cocktail",
          "Coffee Break",
          "Box Lunch",
          "BBQ",
        ],
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "min_guests",
        label: "Mínimo Invitados",
        type: "number",
        required: true,
        sortOrder: 2,
      },
      {
        key: "price_per_person",
        label: "Precio por Persona",
        type: "number",
        required: true,
        sortOrder: 3,
      },
      {
        key: "dietary",
        label: "Opciones Dietéticas",
        type: "multi_select",
        options: ["Vegetariano", "Vegano", "Sin Gluten", "Halal"],
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "min_guests", "price_per_person"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // HOTEL / HOSPITALITY
  // ═══════════════════════════════════════════════════════════════

  hotel: {
    code: "hotel",
    name: "Hotel / Resort",
    icon: "Bed",
    labels: {
      product: "Habitación",
      productPlural: "Habitaciones",
      customer: "Huésped",
      customerPlural: "Huéspedes",
      inventory: "Inventario",
      order: "Reserva",
      orderPlural: "Reservas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Tipo",
      categoryPlural: "Tipos",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: true,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: true,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: true,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "receptionist",
        label: "Recepcionista",
        desc: "Check-in, check-out, reservas",
      },
      {
        code: "housekeeper",
        label: "Camarera de Piso",
        desc: "Limpieza de habitaciones",
      },
      {
        code: "concierge",
        label: "Concierge",
        desc: "Atención personalizada al huésped",
      },
      { code: "bellboy", label: "Bellboy", desc: "Equipaje y asistencia" },
      {
        code: "maintenance",
        label: "Mantenimiento",
        desc: "Reparaciones del hotel",
      },
      { code: "chef", label: "Chef", desc: "Cocina del hotel" },
      {
        code: "pool_attendant",
        label: "Alberca",
        desc: "Mantenimiento de piscina",
      },
    ],
    sidebar: [
      {
        key: "rooms",
        title: "Habitaciones",
        icon: "Bed",
        defaultOpen: true,
        items: [
          { path: "/hub/hotel", label: "Mapa de Habitaciones", icon: "Grid" },
          { path: "/hub/reservations", label: "Reservas", icon: "Calendar" },
          { path: "/hub/customers", label: "Huéspedes", icon: "Users" },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Huésped",
        customerPlural: "Huéspedes",
        inventory: "Inventario",
        order: "Reserva",
        orderPlural: "Reservas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Tipo",
        categoryPlural: "Tipos",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      operationsSection,
      biSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "room_type",
        label: "Tipo de Habitación",
        type: "select",
        options: [
          "Individual",
          "Doble",
          "Triple",
          "Suite",
          "Presidencial",
          "Familiar",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "capacity",
        label: "Capacidad",
        type: "number",
        required: true,
        sortOrder: 2,
      },
      {
        key: "view",
        label: "Vista",
        type: "select",
        options: ["Mar", "Jardín", "Ciudad", "Piscina", "Sin vista"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "bed_type",
        label: "Tipo de Cama",
        type: "select",
        options: ["Individual", "Queen", "King", "2 Camas"],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "includes_breakfast",
        label: "Incluye Desayuno",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "amenities",
        label: "Amenidades",
        type: "multi_select",
        options: [
          "A/C",
          "WiFi",
          "TV",
          "Minibar",
          "Caja Fuerte",
          "Balcón",
          "Jacuzzi",
        ],
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["room_type", "capacity", "view", "includes_breakfast"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // AUTOMOTIVE
  // ═══════════════════════════════════════════════════════════════

  auto_workshop: {
    code: "auto_workshop",
    name: "Taller Mecánico",
    icon: "Wrench",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Repuestos",
      order: "Orden de Trabajo",
      orderPlural: "Órdenes de Trabajo",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: false,
      vehicleIntake: true,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: true,
      warranties: true,
      installations: false,
      crm: true,
      promotions: false,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "mechanic",
        label: "Mecánico",
        desc: "Mecánica general y reparaciones",
      },
      {
        code: "electrician",
        label: "Electricista",
        desc: "Sistema eléctrico automotriz",
      },
      { code: "painter", label: "Pintor", desc: "Pintura y carrocería" },
      { code: "tire_tech", label: "Llantero", desc: "Llantas y alineación" },
      {
        code: "foreman",
        label: "Jefe de Taller",
        desc: "Supervisión de mecánicos",
      },
      {
        code: "receptionist",
        label: "Recepcionista",
        desc: "Recepción de vehículos",
      },
      {
        code: "diagnostician",
        label: "Diagnosticador",
        desc: "Diagnóstico computarizado",
      },
    ],
    sidebar: [
      {
        key: "workshop",
        title: "Taller",
        icon: "Wrench",
        defaultOpen: true,
        items: [
          {
            path: "/hub/workshop",
            label: "Órdenes de Trabajo",
            icon: "ClipboardList",
          },
          { path: "/hub/workshop", label: "Entrada de Vehículos", icon: "Car" },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
          { path: "/hub/warranties", label: "Garantías", icon: "ShieldCheck" },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Repuestos",
        order: "OT",
        orderPlural: "Órdenes de Trabajo",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Repuestos" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      biSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo de Servicio",
        type: "select",
        options: [
          "Mantenimiento",
          "Reparación",
          "Diagnóstico",
          "Alineación",
          "Balanceo",
          "Pintura",
          "A/C",
          "Eléctrica",
          "Llantería",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "vehicle_brand",
        label: "Marca Vehículo",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "vehicle_model",
        label: "Modelo",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "vehicle_year",
        label: "Año",
        type: "text",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "part_number",
        label: "Número de Parte",
        type: "text",
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "warranty_months",
        label: "Garantía (meses)",
        type: "number",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "vehicle_brand", "warranty_months"],
    },
  },

  auto_dealer: {
    code: "auto_dealer",
    name: "Concesionario / Dealer",
    icon: "Car",
    labels: {
      product: "Vehículo",
      productPlural: "Vehículos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Distribuidor",
      supplierPlural: "Distribuidores",
      category: "Marca",
      categoryPlural: "Marcas",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: true,
      serviceOrders: true,
      warranties: true,
      installations: false,
      crm: true,
      promotions: false,
      commissions: true,
      multiBranch: true,
      multiCurrency: true,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "sales_advisor",
        label: "Asesor de Ventas",
        desc: "Ventas de vehículos",
      },
      {
        code: "finance_manager",
        label: "Financiamiento",
        desc: "Créditos y leasing",
      },
      {
        code: "appraiser",
        label: "Avaluador",
        desc: "Avalúo de vehículos usados",
      },
      {
        code: "test_drive",
        label: "Test Drive",
        desc: "Coordinación de pruebas",
      },
    ],
    sidebar: [
      {
        key: "vehicles",
        title: "Vehículos",
        icon: "Car",
        defaultOpen: true,
        items: [
          {
            path: "/sales/products",
            label: "Inventario Vehículos",
            icon: "Car",
          },
          {
            path: "/sales/transactions",
            label: "Ventas",
            icon: "ClipboardList",
          },
          { path: "/hub/test-drive", label: "Test Drive", icon: "Calendar" },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      salesSection({
        product: "Vehículo",
        productPlural: "Vehículos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
        category: "Marca",
        categoryPlural: "Marcas",
      }),
      purchasingSection({
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      biSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "model",
        label: "Modelo",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "year",
        label: "Año",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "vin",
        label: "VIN (Chasis)",
        type: "text",
        showInPOS: false,
        sortOrder: 4,
      },
      {
        key: "color_ext",
        label: "Color Exterior",
        type: "text",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "color_int",
        label: "Color Interior",
        type: "text",
        showInPOS: true,
        sortOrder: 6,
      },
      {
        key: "mileage",
        label: "Kilometraje",
        type: "number",
        showInPOS: true,
        sortOrder: 7,
      },
      {
        key: "fuel_type",
        label: "Combustible",
        type: "select",
        options: ["Gasolina", "Diesel", "Híbrido", "Eléctrico", "GLP"],
        showInPOS: true,
        sortOrder: 8,
      },
      {
        key: "transmission",
        label: "Transmisión",
        type: "select",
        options: ["Manual", "Automática", "CVT"],
        showInPOS: true,
        sortOrder: 9,
      },
      {
        key: "condition",
        label: "Condición",
        type: "select",
        options: ["Nuevo", "Usado", "Certificado"],
        showInPOS: true,
        sortOrder: 10,
      },
      {
        key: "engine",
        label: "Motor",
        type: "text",
        showInPOS: false,
        sortOrder: 11,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: [
        "brand",
        "model",
        "year",
        "color_ext",
        "mileage",
        "fuel_type",
        "transmission",
        "condition",
      ],
    },
  },

  auto_parts: {
    code: "auto_parts",
    name: "Repuestos / Autopartes",
    icon: "Cog",
    labels: {
      product: "Repuesto",
      productPlural: "Repuestos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Pedido",
      orderPlural: "Pedidos",
      supplier: "Distribuidor",
      supplierPlural: "Distribuidores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "parts_advisor",
        label: "Asesor de Partes",
        desc: "Identifica y recomienda repuestos",
      },
      {
        code: "warehouse",
        label: "Almacenista",
        desc: "Control de inventario de repuestos",
      },
    ],
    sidebar: [
      salesSection({
        product: "Repuesto",
        productPlural: "Repuestos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Pedido",
        orderPlural: "Pedidos",
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "oem_number",
        label: "Número OEM",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "compatibility",
        label: "Vehículos Compatibles",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "position",
        label: "Posición",
        type: "select",
        options: [
          "Delantero",
          "Trasero",
          "Izquierdo",
          "Derecho",
          "Superior",
          "Inferior",
          "Universal",
        ],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "condition",
        label: "Condición",
        type: "select",
        options: ["Nuevo", "Reacondicionado", "Usado"],
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "warranty_months",
        label: "Garantía (meses)",
        type: "number",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["oem_number", "compatibility", "brand", "condition"],
    },
  },

  car_rental: {
    code: "car_rental",
    name: "Rent-a-Car",
    icon: "Car",
    labels: {
      product: "Vehículo",
      productPlural: "Flota",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Flota",
      order: "Reserva",
      orderPlural: "Reservas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Clase",
      categoryPlural: "Clases",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: true,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: true,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "agent", label: "Agente", desc: "Atención y reservaciones" },
      {
        code: "detailer",
        label: "Detailer",
        desc: "Limpieza y preparación de vehículos",
      },
      {
        code: "inspector",
        label: "Inspector",
        desc: "Inspección pre/post renta",
      },
    ],
    sidebar: [
      {
        key: "fleet",
        title: "Flota",
        icon: "Car",
        defaultOpen: true,
        items: [
          { path: "/sales/products", label: "Flota de Vehículos", icon: "Car" },
          { path: "/hub/reservations", label: "Reservas", icon: "Calendar" },
          {
            path: "/sales/transactions",
            label: "Rentas Activas",
            icon: "ClipboardList",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      salesSection({
        product: "Vehículo",
        productPlural: "Flota",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Flota",
        order: "Reserva",
        orderPlural: "Reservas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Clase",
        categoryPlural: "Clases",
      }),
      accountingSection,
      taxSection,
      hrSection,
      biSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "model",
        label: "Modelo",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "year",
        label: "Año",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "plate",
        label: "Placa",
        type: "text",
        required: true,
        sortOrder: 4,
      },
      {
        key: "vehicle_class",
        label: "Clase",
        type: "select",
        options: [
          "Económico",
          "Compacto",
          "Sedan",
          "SUV",
          "Van",
          "Premium",
          "Lujo",
        ],
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "daily_rate",
        label: "Tarifa Diaria",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 6,
      },
      {
        key: "fuel_type",
        label: "Combustible",
        type: "select",
        options: ["Gasolina", "Diesel", "Híbrido", "Eléctrico"],
        showInPOS: true,
        sortOrder: 7,
      },
      {
        key: "transmission",
        label: "Transmisión",
        type: "select",
        options: ["Manual", "Automática"],
        showInPOS: true,
        sortOrder: 8,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: [
        "brand",
        "model",
        "vehicle_class",
        "daily_rate",
        "fuel_type",
        "transmission",
      ],
    },
  },

  gas_station: {
    code: "gas_station",
    name: "Estación de Combustible",
    icon: "Fuel",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Distribuidor",
      supplierPlural: "Distribuidores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "pump_attendant",
        label: "Despachador",
        desc: "Despacho de combustible",
      },
      {
        code: "station_manager",
        label: "Jefe de Estación",
        desc: "Gerencia de la estación",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "fuel_type",
        label: "Tipo Combustible",
        type: "select",
        options: ["Gasolina Premium", "Gasolina Regular", "Diesel", "GLP"],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "volume_liters",
        label: "Volumen (litros)",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "octane",
        label: "Octanaje",
        type: "select",
        options: ["87 Regular", "91 Premium", "N/A"],
        showInPOS: true,
        sortOrder: 3,
      },
    ],
    pos: {
      layout: "counter",
      showCategories: false,
      showSearch: false,
      showImages: false,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["fuel_type", "volume_liters"],
    },
  },

  car_wash: {
    code: "car_wash",
    name: "Car Wash / Lavado",
    icon: "Droplets",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Insumos",
      order: "Orden",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: true,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "washer", label: "Lavador(a)", desc: "Lavado de vehículos" },
      { code: "detailer", label: "Detailer", desc: "Detallado y pulido" },
    ],
    sidebar: [
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Insumos",
        order: "Orden",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo de Servicio",
        type: "select",
        options: [
          "Básico",
          "Completo",
          "Detallado",
          "Polichado",
          "Encerado",
          "Motor",
          "Cerámico",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "vehicle_type",
        label: "Tipo Vehículo",
        type: "select",
        options: ["Sedan", "SUV", "Camioneta", "Pickup", "Moto"],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "duration",
        label: "Duración (min)",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: false,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: true,
      splitBill: false,
      tipPrompt: true,
      customFields: ["service_type", "vehicle_type"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // HEALTHCARE
  // ═══════════════════════════════════════════════════════════════

  pharmacy: {
    code: "pharmacy",
    name: "Farmacia",
    icon: "Pill",
    labels: {
      product: "Medicamento",
      productPlural: "Medicamentos",
      customer: "Paciente",
      customerPlural: "Pacientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Distribuidor",
      supplierPlural: "Distribuidores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: true,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: true,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "pharmacist",
        label: "Farmacéutico(a)",
        desc: "Dispensación y consejo farmacéutico",
      },
      {
        code: "pharmacy_tech",
        label: "Técnico Farmacéutico",
        desc: "Asistente del farmacéutico",
      },
    ],
    sidebar: [
      salesSection({
        product: "Medicamento",
        productPlural: "Medicamentos",
        customer: "Paciente",
        customerPlural: "Pacientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "rx",
        title: "Recetas",
        icon: "FileText",
        items: [
          { path: "/hub/service-orders", label: "Recetas", icon: "FileText" },
          { path: "/hub/customers", label: "Pacientes", icon: "Users" },
        ],
      },
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "active_ingredient",
        label: "Principio Activo",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "dosage",
        label: "Dosis",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "form",
        label: "Forma Farmacéutica",
        type: "select",
        options: [
          "Tableta",
          "Cápsula",
          "Jarabe",
          "Inyectable",
          "Crema",
          "Gotas",
          "Supositorio",
        ],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "laboratory",
        label: "Laboratorio",
        type: "text",
        showInPOS: false,
        sortOrder: 4,
      },
      {
        key: "registration",
        label: "Registro Sanitario",
        type: "text",
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "controlled",
        label: "Sustancia Controlada",
        type: "boolean",
        showInPOS: true,
        sortOrder: 6,
      },
      {
        key: "requires_rx",
        label: "Requiere Receta",
        type: "boolean",
        showInPOS: true,
        sortOrder: 7,
      },
      {
        key: "expiration",
        label: "Vencimiento",
        type: "date",
        showInPOS: false,
        sortOrder: 8,
      },
      {
        key: "lot",
        label: "Lote",
        type: "text",
        showInPOS: false,
        sortOrder: 9,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["active_ingredient", "dosage", "form", "requires_rx"],
    },
  },

  clinic: {
    code: "clinic",
    name: "Clínica / Consultorio",
    icon: "Stethoscope",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Paciente",
      customerPlural: "Pacientes",
      inventory: "Insumos Médicos",
      order: "Cita",
      orderPlural: "Citas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Especialidad",
      categoryPlural: "Especialidades",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: true,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: true,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "doctor",
        label: "Doctor(a)",
        desc: "Médico general o especialista",
      },
      {
        code: "nurse",
        label: "Enfermera(o)",
        desc: "Asistencia médica y enfermería",
      },
      { code: "lab_tech", label: "Laboratorista", desc: "Análisis clínicos" },
      {
        code: "radiologist",
        label: "Radiólogo(a)",
        desc: "Imágenes diagnósticas",
      },
      {
        code: "receptionist",
        label: "Recepcionista",
        desc: "Citas y recepción",
      },
    ],
    sidebar: [
      {
        key: "patients",
        title: "Pacientes",
        icon: "Users",
        defaultOpen: true,
        items: [
          { path: "/hub/clinic", label: "Expedientes", icon: "Users" },
          {
            path: "/hub/reservations",
            label: "Agenda de Citas",
            icon: "Calendar",
          },
          { path: "/hub/service-orders", label: "Recetas", icon: "FileText" },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Paciente",
        customerPlural: "Pacientes",
        inventory: "Insumos",
        order: "Cita",
        orderPlural: "Citas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Especialidad",
        categoryPlural: "Especialidades",
      }),
      inventorySection({ inventory: "Insumos Médicos" } as any),
      accountingSection,
      taxSection,
      hrSection,
      biSection,
    ],
    productFields: [
      {
        key: "specialty",
        label: "Especialidad",
        type: "select",
        options: [
          "Medicina General",
          "Pediatría",
          "Ginecología",
          "Cardiología",
          "Dermatología",
          "Oftalmología",
          "Ortopedia",
          "Otra",
        ],
        required: true,
        sortOrder: 1,
      },
      {
        key: "duration_min",
        label: "Duración (min)",
        type: "number",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "insurance",
        label: "Seguros Aceptados",
        type: "multi_select",
        options: [
          "ARS Humano",
          "ARS Palic",
          "ARS Universal",
          "ARS Senasa",
          "Particular",
        ],
        sortOrder: 3,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["specialty", "duration_min"],
    },
  },

  dental: {
    code: "dental",
    name: "Consultorio Dental",
    icon: "Smile",
    labels: {
      product: "Procedimiento",
      productPlural: "Procedimientos",
      customer: "Paciente",
      customerPlural: "Pacientes",
      inventory: "Insumos Dentales",
      order: "Cita",
      orderPlural: "Citas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: true,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: true,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "dentist",
        label: "Dentista",
        desc: "Odontólogo general o especialista",
      },
      {
        code: "orthodontist",
        label: "Ortodoncista",
        desc: "Especialista en ortodoncia",
      },
      {
        code: "dental_assistant",
        label: "Asistente Dental",
        desc: "Asistencia en consultorio",
      },
      {
        code: "hygienist",
        label: "Higienista",
        desc: "Limpieza dental profesional",
      },
      {
        code: "receptionist",
        label: "Recepcionista",
        desc: "Citas y facturación",
      },
    ],
    sidebar: [
      {
        key: "patients",
        title: "Pacientes",
        icon: "Users",
        defaultOpen: true,
        items: [
          { path: "/hub/dental", label: "Expedientes", icon: "Users" },
          { path: "/hub/odontogram", label: "Odontograma", icon: "Smile" },
          { path: "/hub/reservations", label: "Agenda", icon: "Calendar" },
          {
            path: "/hub/dental",
            label: "Plan de Tratamiento",
            icon: "ClipboardList",
          },
        ],
      },
      salesSection({
        product: "Procedimiento",
        productPlural: "Procedimientos",
        customer: "Paciente",
        customerPlural: "Pacientes",
        inventory: "Insumos",
        order: "Cita",
        orderPlural: "Citas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Insumos Dentales" } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "procedure",
        label: "Procedimiento",
        type: "select",
        options: [
          "Limpieza",
          "Extracción",
          "Empaste",
          "Corona",
          "Implante",
          "Blanqueamiento",
          "Ortodoncia",
          "Endodoncia",
          "Radiografía",
          "Consulta",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "material",
        label: "Material",
        type: "select",
        options: ["Resina", "Porcelana", "Metal", "Zirconia", "Titanio", "N/A"],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "sessions",
        label: "Sesiones",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "warranty_years",
        label: "Garantía (años)",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["procedure", "material", "sessions"],
    },
  },

  optics: {
    code: "optics",
    name: "Óptica",
    icon: "Glasses",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Pedido",
      orderPlural: "Pedidos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: true,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: true,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: true,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "optometrist",
        label: "Optometrista",
        desc: "Examen visual y graduación",
      },
      {
        code: "sales_advisor",
        label: "Asesor(a)",
        desc: "Venta de monturas y lentes",
      },
      {
        code: "lab_tech",
        label: "Técnico de Laboratorio",
        desc: "Fabricación de lentes",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Pedido",
        orderPlural: "Pedidos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "rx",
        title: "Recetas",
        icon: "FileText",
        items: [
          { path: "/hub/customers", label: "Pacientes", icon: "Users" },
          {
            path: "/hub/reservations",
            label: "Citas Examen",
            icon: "Calendar",
          },
          {
            path: "/hub/service-orders",
            label: "Pedidos de Lentes",
            icon: "ClipboardList",
          },
        ],
      },
      inventorySection({ inventory: "Inventario" } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "frame_type",
        label: "Tipo de Montura",
        type: "select",
        options: [
          "Completa",
          "Sin Marco",
          "Semi-armada",
          "Deportiva",
          "Lente de Contacto",
        ],
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "lens_type",
        label: "Tipo de Lente",
        type: "select",
        options: [
          "Monofocal",
          "Bifocal",
          "Progresivo",
          "Fotocromático",
          "Antirreflejante",
          "Solo Montura",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "material",
        label: "Material",
        type: "select",
        options: [
          "CR-39",
          "Policarbonato",
          "Vidrio",
          "Trivex",
          "Metal",
          "Acetato",
          "Titanio",
        ],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["frame_type", "lens_type", "color", "material"],
    },
  },

  veterinary: {
    code: "veterinary",
    name: "Veterinaria",
    icon: "HeartPulse",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Dueño",
      customerPlural: "Dueños",
      inventory: "Medicamentos",
      order: "Cita",
      orderPlural: "Citas",
      supplier: "Distribuidor",
      supplierPlural: "Distribuidores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: true,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: true,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "vet", label: "Veterinario(a)", desc: "Médico veterinario" },
      {
        code: "vet_assistant",
        label: "Asistente Vet",
        desc: "Asistencia en consultas",
      },
      { code: "groomer", label: "Groomer", desc: "Estética animal" },
    ],
    sidebar: [
      {
        key: "patients",
        title: "Mascotas",
        icon: "Heart",
        defaultOpen: true,
        items: [
          {
            path: "/hub/veterinary",
            label: "Dueños y Mascotas",
            icon: "Users",
          },
          { path: "/hub/reservations", label: "Agenda", icon: "Calendar" },
          {
            path: "/hub/service-orders",
            label: "Expedientes",
            icon: "FileText",
          },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Dueño",
        customerPlural: "Dueños",
        inventory: "Medicamentos",
        order: "Cita",
        orderPlural: "Citas",
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Medicamentos" } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo de Servicio",
        type: "select",
        options: [
          "Consulta",
          "Vacunación",
          "Cirugía",
          "Desparasitación",
          "Laboratorio",
          "Grooming",
          "Emergencia",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "animal_type",
        label: "Tipo de Animal",
        type: "select",
        options: ["Perro", "Gato", "Ave", "Reptil", "Roedor", "Exótico"],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "vaccine_brand",
        label: "Marca Vacuna",
        type: "text",
        showInPOS: false,
        sortOrder: 3,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "animal_type"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // CONSTRUCTION & HOME
  // ═══════════════════════════════════════════════════════════════

  hardware: {
    code: "hardware",
    name: "Ferretería",
    icon: "Wrench",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Pedido",
      orderPlural: "Pedidos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "sales_advisor",
        label: "Asesor de Ventas",
        desc: "Asesoría técnica al cliente",
      },
      { code: "warehouse", label: "Almacenista", desc: "Control de bodega" },
      {
        code: "loader",
        label: "Cargador",
        desc: "Carga y descarga de materiales",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Pedido",
        orderPlural: "Pedidos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "material",
        label: "Material",
        type: "select",
        options: [
          "Acero",
          "Aluminio",
          "PVC",
          "Cobre",
          "Madera",
          "Hormigón",
          "Otro",
        ],
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "measure",
        label: "Medida",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "unit",
        label: "Unidad",
        type: "select",
        options: [
          "Metro",
          "Pie",
          "Kg",
          "Libra",
          "Unidad",
          "Galón",
          "Litro",
          "Saco",
          "Caja",
        ],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "finish",
        label: "Acabado",
        type: "select",
        options: [
          "Natural",
          "Galvanizado",
          "Pintado",
          "Pulido",
          "Cromado",
          "Sin acabado",
        ],
        showInPOS: false,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["material", "measure", "unit", "brand"],
    },
  },

  furniture: {
    code: "furniture",
    name: "Mueblería",
    icon: "Sofa",
    labels: {
      product: "Mueble",
      productPlural: "Muebles",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Pedido",
      orderPlural: "Pedidos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Ambiente",
      categoryPlural: "Ambientes",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: true,
      installations: true,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "sales_advisor", label: "Asesor(a)", desc: "Ventas y diseño" },
      { code: "delivery", label: "Entregador", desc: "Entrega e instalación" },
      { code: "assembler", label: "Ensamblador", desc: "Armado de muebles" },
    ],
    sidebar: [
      salesSection({
        product: "Mueble",
        productPlural: "Muebles",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Pedido",
        orderPlural: "Pedidos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Ambiente",
        categoryPlural: "Ambientes",
      }),
      postSaleSection,
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "material",
        label: "Material",
        type: "select",
        options: [
          "Madera Maciza",
          "MDF",
          "Melamina",
          "Metal",
          "Vidrio",
          "Rattan",
          "Tapizado",
        ],
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "dimensions",
        label: "Dimensiones (LxAxA)",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "room",
        label: "Ambiente",
        type: "select",
        options: [
          "Sala",
          "Comedor",
          "Dormitorio",
          "Cocina",
          "Oficina",
          "Exterior",
          "Baño",
        ],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "style",
        label: "Estilo",
        type: "select",
        options: ["Moderno", "Clásico", "Rústico", "Industrial", "Minimalista"],
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "requires_assembly",
        label: "Requiere Ensamblaje",
        type: "boolean",
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["material", "color", "dimensions", "room", "style"],
    },
  },

  construction: {
    code: "construction",
    name: "Constructora",
    icon: "HardHat",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Materiales",
      order: "Proyecto",
      orderPlural: "Proyectos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: false,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: true,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: true,
      warranties: true,
      installations: false,
      crm: true,
      promotions: false,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "engineer", label: "Ingeniero(a)", desc: "Ingeniero de obra" },
      { code: "architect", label: "Arquitecto(a)", desc: "Diseño y planos" },
      { code: "foreman", label: "Capataz", desc: "Supervisión de obra" },
      { code: "mason", label: "Albañil", desc: "Construcción y acabados" },
      {
        code: "electrician",
        label: "Electricista",
        desc: "Instalaciones eléctricas",
      },
      { code: "plumber", label: "Plomero", desc: "Instalaciones hidráulicas" },
      { code: "welder", label: "Soldador", desc: "Estructuras metálicas" },
      { code: "painter", label: "Pintor", desc: "Pintura y acabados" },
      { code: "operator", label: "Operador", desc: "Maquinaria pesada" },
    ],
    sidebar: [
      {
        key: "projects",
        title: "Proyectos",
        icon: "HardHat",
        defaultOpen: true,
        items: [
          { path: "/hub/construction", label: "Proyectos", icon: "HardHat" },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
          {
            path: "/hub/installations",
            label: "Avance de Obra",
            icon: "TrendingUp",
          },
        ],
      },
      inventorySection({ inventory: "Materiales" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      biSection,
    ],
    productFields: [
      {
        key: "material",
        label: "Material",
        type: "text",
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "measure",
        label: "Medida",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "unit",
        label: "Unidad",
        type: "select",
        options: ["Metro", "m²", "m³", "Saco", "Kg", "Litro", "Pieza", "Galón"],
        showInPOS: true,
        sortOrder: 3,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["material", "measure", "unit"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // RETAIL
  // ═══════════════════════════════════════════════════════════════

  clothing: {
    code: "clothing",
    name: "Tienda de Ropa",
    icon: "Shirt",
    labels: {
      product: "Prenda",
      productPlural: "Prendas",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "sales_advisor",
        label: "Asesor(a) de Moda",
        desc: "Atención y asesoría al cliente",
      },
      { code: "tailor", label: "Costurero(a)", desc: "Arreglos y ajustes" },
      {
        code: "visual_merchandiser",
        label: "Visual Merchandiser",
        desc: "Escaparatismo y exhibición",
      },
    ],
    sidebar: [
      salesSection({
        product: "Prenda",
        productPlural: "Prendas",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "size",
        label: "Talla",
        type: "select",
        options: [
          "XS",
          "S",
          "M",
          "L",
          "XL",
          "XXL",
          "28",
          "30",
          "32",
          "34",
          "36",
          "38",
          "40",
          "42",
        ],
        required: true,
        showInPOS: true,
        isVariant: true,
        sortOrder: 1,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        required: true,
        showInPOS: true,
        isVariant: true,
        sortOrder: 2,
      },
      {
        key: "gender",
        label: "Género",
        type: "select",
        options: ["Hombre", "Mujer", "Unisex", "Niño", "Niña"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "material",
        label: "Material",
        type: "select",
        options: [
          "Algodón",
          "Poliéster",
          "Seda",
          "Lino",
          "Denim",
          "Cuero",
          "Mezcla",
        ],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "season",
        label: "Temporada",
        type: "select",
        options: ["Primavera", "Verano", "Otoño", "Invierno", "Todo el año"],
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["size", "color", "gender", "material"],
    },
  },

  shoes: {
    code: "shoes",
    name: "Zapatería",
    icon: "Footprints",
    labels: {
      product: "Calzado",
      productPlural: "Calzados",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "sales_advisor",
        label: "Asesor(a)",
        desc: "Atención al cliente",
      },
    ],
    sidebar: [
      salesSection({
        product: "Calzado",
        productPlural: "Calzados",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "size_us",
        label: "Talla US",
        type: "select",
        options: [
          "5",
          "5.5",
          "6",
          "6.5",
          "7",
          "7.5",
          "8",
          "8.5",
          "9",
          "9.5",
          "10",
          "10.5",
          "11",
          "12",
          "13",
        ],
        required: true,
        showInPOS: true,
        isVariant: true,
        sortOrder: 1,
      },
      {
        key: "size_eu",
        label: "Talla EU",
        type: "text",
        showInPOS: false,
        sortOrder: 2,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        isVariant: true,
        sortOrder: 3,
      },
      {
        key: "shoe_type",
        label: "Tipo",
        type: "select",
        options: [
          "Sneaker",
          "Tacón",
          "Sandalia",
          "Bota",
          "Mocasín",
          "Deportivo",
          "Formal",
          "Casual",
        ],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "material",
        label: "Material",
        type: "select",
        options: ["Cuero", "Sintético", "Tela", "Lona", "Goma", "Ante"],
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "width",
        label: "Ancho",
        type: "select",
        options: ["Estrecho", "Regular", "Ancho", "Extra Ancho"],
        showInPOS: false,
        sortOrder: 6,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 7,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["size_us", "color", "shoe_type", "material"],
    },
  },

  jewelry: {
    code: "jewelry",
    name: "Joyería / Relojería",
    icon: "Gem",
    labels: {
      product: "Joya",
      productPlural: "Joyas",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: true,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "jeweler",
        label: "Joyer(o/a)",
        desc: "Fabricación y reparación de joyas",
      },
      {
        code: "sales_advisor",
        label: "Asesor(a)",
        desc: "Ventas especializadas",
      },
      {
        code: "gemologist",
        label: "Gemólogo(a)",
        desc: "Evaluación de piedras",
      },
    ],
    sidebar: [
      salesSection({
        product: "Joya",
        productPlural: "Joyas",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      postSaleSection,
      inventorySection({ inventory: "Inventario" } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "metal",
        label: "Metal",
        type: "select",
        options: [
          "Oro 10K",
          "Oro 14K",
          "Oro 18K",
          "Plata 925",
          "Platino",
          "Acero Inoxidable",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "stone",
        label: "Piedra",
        type: "select",
        options: [
          "Diamante",
          "Zafiro",
          "Esmeralda",
          "Ruby",
          "Perla",
          "Topacio",
          "Ninguna",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "karats",
        label: "Quilates",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "weight_g",
        label: "Peso (g)",
        type: "number",
        showInPOS: false,
        sortOrder: 4,
      },
      {
        key: "size",
        label: "Talla (anillo)",
        type: "text",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "certification",
        label: "Certificación",
        type: "text",
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["metal", "stone", "karats", "size"],
    },
  },

  supermarket: {
    code: "supermarket",
    name: "Supermercado / Colmado",
    icon: "ShoppingCart",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Departamento",
      categoryPlural: "Departamentos",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "stocker",
        label: "Reponedor(a)",
        desc: "Reposición de estantes",
      },
      {
        code: "butcher",
        label: "Carnicero(a)",
        desc: "Departamento de carnes",
      },
      {
        code: "baker",
        label: "Panadero(a)",
        desc: "Departamento de panadería",
      },
      {
        code: "department_head",
        label: "Jefe de Depto",
        desc: "Supervisión de departamento",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Departamento",
        categoryPlural: "Departamentos",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      operationsSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "barcode",
        label: "Código de Barras",
        type: "text",
        showInPOS: false,
        sortOrder: 2,
      },
      {
        key: "weight",
        label: "Peso/Volumen",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "expiration",
        label: "Vencimiento",
        type: "date",
        showInPOS: false,
        sortOrder: 4,
      },
      {
        key: "origin",
        label: "País de Origen",
        type: "text",
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "is_organic",
        label: "Orgánico",
        type: "boolean",
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["brand", "weight"],
    },
  },

  cosmetics: {
    code: "cosmetics",
    name: "Cosméticos / Belleza",
    icon: "Sparkles",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientas",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "beauty_advisor",
        label: "Asesora de Belleza",
        desc: "Asesoría y venta",
      },
      {
        code: "makeup_artist",
        label: "Maquilladora",
        desc: "Servicio de maquillaje",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientas",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "shade",
        label: "Tono / Shade",
        type: "text",
        showInPOS: true,
        isVariant: true,
        sortOrder: 2,
      },
      {
        key: "skin_type",
        label: "Tipo de Piel",
        type: "select",
        options: ["Grasa", "Seca", "Mixta", "Sensible", "Normal", "Todas"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "content",
        label: "Contenido",
        type: "text",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "cruelty_free",
        label: "Cruelty Free",
        type: "boolean",
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "ingredients",
        label: "Ingrediente Principal",
        type: "text",
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["brand", "shade", "skin_type", "content"],
    },
  },

  electronics: {
    code: "electronics",
    name: "Electrónica / Tecnología",
    icon: "Laptop",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: true,
      installations: true,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "sales_advisor",
        label: "Asesor(a)",
        desc: "Ventas de tecnología",
      },
      {
        code: "tech_support",
        label: "Soporte Técnico",
        desc: "Reparación y soporte",
      },
      {
        code: "installer",
        label: "Instalador",
        desc: "Instalación de equipos",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      postSaleSection,
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "model",
        label: "Modelo",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "serial",
        label: "Número de Serie",
        type: "text",
        showInPOS: false,
        sortOrder: 3,
      },
      {
        key: "specs",
        label: "Especificaciones",
        type: "text",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        isVariant: true,
        sortOrder: 5,
      },
      {
        key: "warranty_months",
        label: "Garantía (meses)",
        type: "number",
        showInPOS: true,
        sortOrder: 6,
      },
      {
        key: "voltage",
        label: "Voltaje",
        type: "select",
        options: ["110V", "220V", "Dual", "Batería", "USB"],
        showInPOS: false,
        sortOrder: 7,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["brand", "model", "specs", "color", "warranty_months"],
    },
  },

  cell_phone_store: {
    code: "cell_phone_store",
    name: "Tienda de Celulares",
    icon: "Smartphone",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "sales_advisor", label: "Asesor(a)", desc: "Ventas de equipos" },
      {
        code: "tech_repair",
        label: "Técnico Reparación",
        desc: "Reparación de celulares",
      },
      { code: "activation", label: "Activador", desc: "Activación de planes" },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      postSaleSection,
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "model",
        label: "Modelo",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "imei",
        label: "IMEI",
        type: "text",
        showInPOS: false,
        sortOrder: 3,
      },
      {
        key: "storage",
        label: "Almacenamiento",
        type: "select",
        options: ["32GB", "64GB", "128GB", "256GB", "512GB", "1TB"],
        showInPOS: true,
        isVariant: true,
        sortOrder: 4,
      },
      {
        key: "ram",
        label: "RAM (GB)",
        type: "number",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        isVariant: true,
        sortOrder: 6,
      },
      {
        key: "condition",
        label: "Condición",
        type: "select",
        options: ["Nuevo", "Usado", "Reacondicionado"],
        showInPOS: true,
        sortOrder: 7,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["brand", "model", "storage", "color", "condition"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // SERVICES
  // ═══════════════════════════════════════════════════════════════

  salon: {
    code: "salon",
    name: "Salón de Belleza / Barbería",
    icon: "Scissors",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Productos",
      order: "Cita",
      orderPlural: "Citas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: true,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "stylist", label: "Estilista", desc: "Corte, color, peinado" },
      { code: "barber", label: "Barbero", desc: "Corte de caballero" },
      { code: "nail_tech", label: "Manicurista", desc: "Manicure y pedicure" },
      {
        code: "esthetician",
        label: "Esteticista",
        desc: "Tratamientos faciales y corporales",
      },
      { code: "receptionist", label: "Recepcionista", desc: "Citas y caja" },
    ],
    sidebar: [
      {
        key: "appointments",
        title: "Agenda",
        icon: "Calendar",
        defaultOpen: true,
        items: [
          {
            path: "/hub/reservations",
            label: "Agenda de Citas",
            icon: "Calendar",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Productos",
        order: "Cita",
        orderPlural: "Citas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Productos" } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_name",
        label: "Servicio",
        type: "select",
        options: [
          "Corte Dama",
          "Corte Caballero",
          "Tinte",
          "Mechas",
          "Peinado",
          "Alisado",
          "Keratina",
          "Manicure",
          "Pedicure",
          "Facial",
          "Depilación",
          "Cejas",
          "Pestañas",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "duration_min",
        label: "Duración (min)",
        type: "number",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "gender",
        label: "Para",
        type: "select",
        options: ["Dama", "Caballero", "Unisex", "Niño/a"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "hair_length",
        label: "Largo de Cabello",
        type: "select",
        options: ["Corto", "Medio", "Largo", "Extra Largo", "N/A"],
        showInPOS: true,
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: true,
      splitBill: false,
      tipPrompt: true,
      customFields: ["service_name", "duration_min", "gender"],
    },
  },

  gym: {
    code: "gym",
    name: "Gimnasio / Fitness",
    icon: "Dumbbell",
    labels: {
      product: "Plan",
      productPlural: "Planes",
      customer: "Socio",
      customerPlural: "Socios",
      inventory: "Equipos",
      order: "Membresía",
      orderPlural: "Membresías",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: true,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "trainer",
        label: "Entrenador(a)",
        desc: "Entrenamiento personal",
      },
      { code: "instructor", label: "Instructor(a)", desc: "Clases grupales" },
      {
        code: "nutritionist",
        label: "Nutricionista",
        desc: "Planes alimenticios",
      },
      {
        code: "receptionist",
        label: "Recepcionista",
        desc: "Recepción y check-in",
      },
    ],
    sidebar: [
      {
        key: "members",
        title: "Socios",
        icon: "Users",
        defaultOpen: true,
        items: [
          { path: "/hub/gym", label: "Socios", icon: "Users" },
          {
            path: "/hub/reservations",
            label: "Clases Grupales",
            icon: "Calendar",
          },
          {
            path: "/hub/service-orders",
            label: "Membresías",
            icon: "CreditCard",
          },
        ],
      },
      salesSection({
        product: "Plan",
        productPlural: "Planes",
        customer: "Socio",
        customerPlural: "Socios",
        inventory: "Equipos",
        order: "Membresía",
        orderPlural: "Membresías",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "plan_type",
        label: "Tipo de Plan",
        type: "select",
        options: [
          "Mensual",
          "Trimestral",
          "Semestral",
          "Anual",
          "Clase Individual",
          "10 Clases",
          "Day Pass",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "class_type",
        label: "Tipo de Clase",
        type: "select",
        options: [
          "Spinning",
          "CrossFit",
          "Yoga",
          "Pilates",
          "Zumba",
          "Boxeo",
          "Funcional",
          "HIIT",
          "Natación",
          "Musculación",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "includes_trainer",
        label: "Incluye Entrenador",
        type: "boolean",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "access_hours",
        label: "Horario",
        type: "select",
        options: ["24/7", "6am-10pm", "8am-8pm", "Horario de Clase"],
        showInPOS: true,
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["plan_type", "class_type", "includes_trainer"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // MORE RETAIL
  // ═══════════════════════════════════════════════════════════════

  department_store: {
    code: "department_store",
    name: "Tienda por Departamentos",
    icon: "Store",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Departamento",
      categoryPlural: "Departamentos",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "sales_advisor",
        label: "Asesor(a)",
        desc: "Atención al cliente por departamento",
      },
      {
        code: "dept_head",
        label: "Jefe de Depto",
        desc: "Supervisión de departamento",
      },
      {
        code: "visual",
        label: "Visual Merchandiser",
        desc: "Exhibición de productos",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Departamento",
        categoryPlural: "Departamentos",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "size",
        label: "Talla",
        type: "text",
        showInPOS: true,
        isVariant: true,
        sortOrder: 2,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        isVariant: true,
        sortOrder: 3,
      },
      {
        key: "department",
        label: "Departamento",
        type: "select",
        options: [
          "Ropa",
          "Hogar",
          "Electrónica",
          "Deportes",
          "Juguetes",
          "Belleza",
          "Línea Blanca",
          "Otros",
        ],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "season",
        label: "Temporada",
        type: "select",
        options: ["Primavera", "Verano", "Otoño", "Invierno", "Todo el año"],
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["brand", "size", "color"],
    },
  },

  pet_shop: {
    code: "pet_shop",
    name: "Tienda de Mascotas",
    icon: "PawPrint",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Dueño",
      customerPlural: "Dueños",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "pet_advisor",
        label: "Asesor(a)",
        desc: "Asesoría sobre productos por mascota",
      },
      { code: "groomer", label: "Groomer", desc: "Estética animal" },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Dueño",
        customerPlural: "Dueños",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "pets",
        title: "Mascotas",
        icon: "Heart",
        items: [
          {
            path: "/hub/veterinary",
            label: "Dueños y Mascotas",
            icon: "Users",
          },
        ],
      },
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "pet_type",
        label: "Tipo de Mascota",
        type: "select",
        options: ["Perro", "Gato", "Ave", "Pez", "Reptil", "Roedor", "Otro"],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "size",
        label: "Tamaño",
        type: "select",
        options: ["Mini", "Pequeño", "Mediano", "Grande", "Gigante"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "life_stage",
        label: "Etapa",
        type: "select",
        options: ["Cachorro", "Adulto", "Senior", "Todas"],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "flavor",
        label: "Sabor",
        type: "text",
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "weight_kg",
        label: "Peso (kg)",
        type: "number",
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["pet_type", "brand", "size", "life_stage"],
    },
  },

  sports: {
    code: "sports",
    name: "Tienda Deportiva",
    icon: "Dumbbell",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Deporte",
      categoryPlural: "Deportes",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "sales_advisor", label: "Asesor(a)", desc: "Asesoría deportiva" },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Deporte",
        categoryPlural: "Deportes",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "sport",
        label: "Deporte",
        type: "select",
        options: [
          "Fútbol",
          "Basketball",
          "Baseball",
          "Tennis",
          "Natación",
          "Running",
          "Cycling",
          "Boxeo",
          "Gym",
          "Volleyball",
          "Otro",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "size",
        label: "Talla",
        type: "select",
        options: ["XS", "S", "M", "L", "XL", "XXL", "Única"],
        showInPOS: true,
        isVariant: true,
        sortOrder: 3,
      },
      {
        key: "gender",
        label: "Género",
        type: "select",
        options: ["Hombre", "Mujer", "Unisex"],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        isVariant: true,
        sortOrder: 5,
      },
      {
        key: "material",
        label: "Material",
        type: "text",
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["sport", "brand", "size", "gender"],
    },
  },

  toys: {
    code: "toys",
    name: "Juguetería",
    icon: "Gamepad2",
    labels: {
      product: "Juguete",
      productPlural: "Juguetes",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "sales_advisor",
        label: "Asesor(a)",
        desc: "Recomendación por edad",
      },
    ],
    sidebar: [
      salesSection({
        product: "Juguete",
        productPlural: "Juguetes",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "age_range",
        label: "Rango de Edad",
        type: "select",
        options: [
          "0-2 años",
          "3-5 años",
          "6-8 años",
          "9-12 años",
          "13+",
          "Todas",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "category",
        label: "Categoría",
        type: "select",
        options: [
          "Didáctico",
          "Electrónico",
          "Muñecas",
          "Vehículos",
          "Construcción",
          "Juegos de Mesa",
          "Peluches",
          "Aire libre",
        ],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "material",
        label: "Material",
        type: "select",
        options: ["Plástico", "Madera", "Tela", "Metal", "Mixto"],
        showInPOS: false,
        sortOrder: 4,
      },
      {
        key: "battery",
        label: "Requiere Baterías",
        type: "boolean",
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "license",
        label: "Licencia/Personaje",
        type: "text",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["age_range", "brand", "category", "license"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // FOOD SHOPS
  // ═══════════════════════════════════════════════════════════════

  butcher: {
    code: "butcher",
    name: "Carnicería",
    icon: "Beef",
    labels: {
      product: "Corte",
      productPlural: "Cortes",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "butcher",
        label: "Carnicero(a)",
        desc: "Corte y preparación de carnes",
      },
      { code: "counter", label: "Mostrador", desc: "Atención al público" },
    ],
    sidebar: [
      {
        key: "sales",
        title: "Ventas y Cobro",
        icon: "DollarSign",
        defaultOpen: true,
        items: [
          { path: "/sales/transactions", label: "Ventas", icon: "ClipboardList" },
          { path: "/pos/linear", label: "Facturación", icon: "FileText" },
          { path: "/sales/products", label: "Cortes", icon: "Tag" },
          { path: "/hub/promotions", label: "Promociones", icon: "Percent" },
          { path: "/hub/print-invoice", label: "Imprimir Facturas", icon: "Printer" },
        ],
      },
      {
        key: "meat-shop",
        title: "Carnicería",
        icon: "Beef",
        defaultOpen: true,
        items: [
          { path: "/pos/visual", label: "POS Carnicería", icon: "Scale" },
          {
            path: "/hub/meat-shop",
            label: "Control de Carnicería",
            icon: "Beef",
          },
          {
            path: "/hub/meat-shop",
            label: "Despiece y Lotes",
            icon: "Scissors",
          },
        ],
      },
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "animal",
        label: "Animal",
        type: "select",
        options: ["Res", "Cerdo", "Pollo", "Pavo", "Chivo", "Cordero"],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "cut",
        label: "Tipo de Corte",
        type: "select",
        options: [
          "Bistec",
          "Costilla",
          "Pierna",
          "Lomo",
          "Pecho",
          "Molida",
          "Entero",
          "Troceado",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "weight_kg",
        label: "Peso (kg)",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "origin",
        label: "Origen",
        type: "select",
        options: ["Nacional", "Importado"],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "quality",
        label: "Calidad",
        type: "select",
        options: ["Primera", "Segunda", "Premium", "Orgánico"],
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "packaging",
        label: "Empaque",
        type: "select",
        options: "Al vacío, bandeja, granel".split(", "),
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "counter",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["animal", "cut", "weight_kg", "origin"],
    },
  },

  fish_market: {
    code: "fish_market",
    name: "Pescadería / Marisquería",
    icon: "Fish",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "fishmonger",
        label: "Pescadero(a)",
        desc: "Preparación y limpieza de pescado",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "species",
        label: "Especie",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "state",
        label: "Estado",
        type: "select",
        options: [
          "Fresco",
          "Congelado",
          "Limpiado",
          "Entero",
          "Fileteado",
          "Ahumado",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "weight_kg",
        label: "Peso (kg)",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "origin",
        label: "Origen",
        type: "select",
        options: ["Nacional", "Importado", "Río", "Mar"],
        showInPOS: true,
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "counter",
      showCategories: true,
      showSearch: false,
      showImages: false,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["species", "state", "weight_kg", "origin"],
    },
  },

  fruit_stand: {
    code: "fruit_stand",
    name: "Frutería / Verdulería",
    icon: "Apple",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "produce",
        label: "Productor(a)",
        desc: "Selección y preparación de productos",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "type",
        label: "Tipo",
        type: "select",
        options: ["Fruta", "Verdura", "Hortaliza", "Hierba", "Tubérculo"],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "variety",
        label: "Variedad",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "origin",
        label: "Origen",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "weight_kg",
        label: "Peso (kg)",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "is_organic",
        label: "Orgánico",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "counter",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["type", "variety", "weight_kg", "is_organic"],
    },
  },

  pastry_shop: {
    code: "pastry_shop",
    name: "Pastelería / Repostería Fina",
    icon: "Cake",
    labels: {
      product: "Postre",
      productPlural: "Postres",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Pedido",
      orderPlural: "Pedidos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: true,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "pastry_chef", label: "Pastelero(a)", desc: "Repostería fina" },
      {
        code: "decorator",
        label: "Decorador(a)",
        desc: "Decoración de tortas",
      },
    ],
    sidebar: [
      salesSection({
        product: "Postre",
        productPlural: "Postres",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Pedido",
        orderPlural: "Pedidos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "orders",
        title: "Pedidos Especiales",
        icon: "Calendar",
        items: [
          {
            path: "/hub/service-orders",
            label: "Pedidos Personalizados",
            icon: "ClipboardList",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      inventorySection({ inventory: "Inventario" } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "type",
        label: "Tipo",
        type: "select",
        options: [
          "Torta",
          "Cupcake",
          "Galleta",
          "Pie",
          "Tarta",
          "Chocolate",
          "Postre Individual",
          "Personalizado",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "flavor",
        label: "Sabor",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "portions",
        label: "Porciones/Personas",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "decoration",
        label: "Decoración",
        type: "text",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "dietary",
        label: "Dieta",
        type: "select",
        options: ["Normal", "Sin Azúcar", "Sin Gluten", "Vegano"],
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: false,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["type", "flavor", "portions"],
    },
  },

  ice_cream: {
    code: "ice_cream",
    name: "Heladería",
    icon: "IceCream",
    labels: {
      product: "Helado",
      productPlural: "Helados",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Sabor",
      categoryPlural: "Sabores",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "ice_cream_maker",
        label: "Heladero(a)",
        desc: "Preparación de helados",
      },
    ],
    sidebar: [
      salesSection({
        product: "Helado",
        productPlural: "Helados",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Sabor",
        categoryPlural: "Sabores",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "flavor",
        label: "Sabor",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "presentation",
        label: "Presentación",
        type: "select",
        options: [
          "Cucurucho",
          "Vaso",
          "Pinta",
          "Cuarto",
          "Galón",
          "Paleta",
          "Sundae",
          "Milkshake",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "base",
        label: "Base",
        type: "select",
        options: ["Crema", "Leche", "Agua/Sorbete", "Vegano"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "scoops",
        label: "Bolas",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "toppings",
        label: "Toppings",
        type: "multi_select",
        options: [
          "Chocolate",
          "Caramelo",
          "Fresa",
          "Chispas",
          "Oreo",
          "Nueces",
          "Sin toppings",
        ],
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "dietary",
        label: "Dieta",
        type: "select",
        options: ["Normal", "Sin Azúcar", "Sin Lactosa", "Vegano"],
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: false,
      showImages: true,
      showPrice: true,
      showStock: false,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["flavor", "presentation", "scoops", "toppings"],
    },
  },

  candy_store: {
    code: "candy_store",
    name: "Dulcería / Confitería",
    icon: "Candy",
    labels: {
      product: "Dulce",
      productPlural: "Dulces",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "sales", label: "Vendedor(a)", desc: "Atención al público" },
    ],
    sidebar: [
      salesSection({
        product: "Dulce",
        productPlural: "Dulces",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "candy_type",
        label: "Tipo",
        type: "select",
        options: [
          "Chocolates",
          "Caramelos",
          "Gomitas",
          "Chicles",
          "Ácidos",
          "Paletas",
          "Turrones",
          "Importados",
          "Locales",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "weight_g",
        label: "Peso (g)",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "sugar_free",
        label: "Sin Azúcar",
        type: "boolean",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "packaging",
        label: "Empaque",
        type: "select",
        options: ["Individual", "Bolsa", "Caja", "Granel", "Regalo"],
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["candy_type", "brand", "weight_g"],
    },
  },

  liquor_store: {
    code: "liquor_store",
    name: "Licorería / Wine Shop",
    icon: "Wine",
    labels: {
      product: "Bebida",
      productPlural: "Bebidas",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Distribuidor",
      supplierPlural: "Distribuidores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "sommelier", label: "Sommelier", desc: "Conocedor de vinos" },
      {
        code: "sales_advisor",
        label: "Asesor(a)",
        desc: "Recomendaciones de bebidas",
      },
    ],
    sidebar: [
      salesSection({
        product: "Bebida",
        productPlural: "Bebidas",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Distribuidor",
        supplierPlural: "Distribuidores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "type",
        label: "Tipo",
        type: "select",
        options: [
          "Cerveza",
          "Ron",
          "Vodka",
          "Whisky",
          "Vino Tinto",
          "Vino Blanco",
          "Tequila",
          "Brandy",
          "Champagne",
          "Licor",
          "Sin Alcohol",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "volume_ml",
        label: "Volumen (ml)",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "abv",
        label: "Graduación %",
        type: "number",
        showInPOS: false,
        sortOrder: 4,
      },
      {
        key: "origin",
        label: "País",
        type: "text",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "vintage",
        label: "Cosecha/Año",
        type: "text",
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["type", "brand", "volume_ml", "origin"],
    },
  },

  coffee_shop: {
    code: "coffee_shop",
    name: "Cafetería / Coffee Shop",
    icon: "Coffee",
    labels: {
      product: "Bebida",
      productPlural: "Bebidas",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Orden",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: true,
      weightPricing: false,
      tipManagement: true,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "barista",
        label: "Barista",
        desc: "Preparación de café especializado",
      },
      { code: "server", label: "Mesero(a)", desc: "Servicio de mesa" },
    ],
    sidebar: [
      salesSection({
        product: "Bebida",
        productPlural: "Bebidas",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Orden",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "drink_type",
        label: "Tipo",
        type: "select",
        options: [
          "Espresso",
          "Americano",
          "Latte",
          "Cappuccino",
          "Mocha",
          "Cold Brew",
          "Té",
          "Chocolate",
          "Smoothie",
          "Jugo",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "size",
        label: "Tamaño",
        type: "select",
        options: ["Small 8oz", "Medium 12oz", "Large 16oz", "XL 20oz"],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "milk",
        label: "Leche",
        type: "select",
        options: [
          "Entera",
          "Descremada",
          "Almendra",
          "Avena",
          "Soja",
          "Sin leche",
        ],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "extras",
        label: "Extras",
        type: "multi_select",
        options: ["Shot Extra", "Vainilla", "Caramelo", "Crema", "Hielo"],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "bean_origin",
        label: "Origen Café",
        type: "text",
        showInPOS: false,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: false,
      showImages: true,
      showPrice: true,
      showStock: false,
      quickPay: true,
      splitBill: false,
      tipPrompt: true,
      customFields: ["drink_type", "size", "milk", "extras"],
    },
  },

  juice_bar: {
    code: "juice_bar",
    name: "Jugos Naturales / Smoothies",
    icon: "Cherry",
    labels: {
      product: "Bebida",
      productPlural: "Bebidas",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Orden",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: true,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "juicer",
        label: "Juicero(a)",
        desc: "Preparación de jugos y smoothies",
      },
    ],
    sidebar: [
      salesSection({
        product: "Bebida",
        productPlural: "Bebidas",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Orden",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "type",
        label: "Tipo",
        type: "select",
        options: [
          "Jugo Natural",
          "Smoothie",
          "Batido",
          "Agua Fresca",
          "Detox",
          "Bowl",
          "Shot Wellness",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "fruits",
        label: "Frutas",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "size_oz",
        label: "Tamaño",
        type: "select",
        options: ["12oz", "16oz", "20oz", "32oz"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "addons",
        label: "Complementos",
        type: "multi_select",
        options: [
          "Proteína",
          "Colágeno",
          "Jengibre",
          "Cúrcuma",
          "Miel",
          "Chía",
          "Granola",
        ],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "no_sugar",
        label: "Sin Azúcar",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: false,
      showImages: true,
      showPrice: true,
      showStock: false,
      quickPay: true,
      splitBill: false,
      tipPrompt: true,
      customFields: ["type", "fruits", "size_oz", "addons"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // CONVENIENCE STORES
  // ═══════════════════════════════════════════════════════════════

  bodega: {
    code: "bodega",
    name: "Bodega / Almacén",
    icon: "Warehouse",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "stocker",
        label: "Reponedor(a)",
        desc: "Reposición de estantes",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "unit",
        label: "Unidad/Tamaño",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "barcode",
        label: "Código de Barras",
        type: "text",
        showInPOS: false,
        sortOrder: 3,
      },
      {
        key: "expiration",
        label: "Vencimiento",
        type: "date",
        showInPOS: false,
        sortOrder: 4,
      },
      {
        key: "is_local",
        label: "Producto Local",
        type: "boolean",
        showInPOS: false,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["brand", "unit"],
    },
  },

  wholesale: {
    code: "wholesale",
    name: "Mayorista / Distribuidora",
    icon: "Truck",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Pedido",
      orderPlural: "Pedidos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: true,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "sales_advisor",
        label: "Ejecutivo de Ventas",
        desc: "Ventas mayoristas",
      },
      { code: "warehouse", label: "Almacenista", desc: "Control de bodega" },
      { code: "loader", label: "Cargador", desc: "Carga y descarga" },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Pedido",
        orderPlural: "Pedidos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "pack_size",
        label: "Tamaño Pack",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "units_per_box",
        label: "Unidades/Caja",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "min_order",
        label: "Pedido Mínimo",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "lot",
        label: "Lote",
        type: "text",
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "weight_total",
        label: "Peso Total",
        type: "number",
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["brand", "pack_size", "units_per_box", "min_order"],
    },
  },

  colmado: {
    code: "colmado",
    name: "Colmado / Mini Market",
    icon: "Store",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "counter",
        label: "Despachador(a)",
        desc: "Atención en mostrador",
      },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "unit",
        label: "Tamaño",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "is_local",
        label: "Producto Local",
        type: "boolean",
        showInPOS: false,
        sortOrder: 3,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["brand", "unit"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // SERVICES
  // ═══════════════════════════════════════════════════════════════

  photo_studio: {
    code: "photo_studio",
    name: "Estudio Fotográfico",
    icon: "Camera",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Equipos",
      order: "Sesión",
      orderPlural: "Sesiones",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: true,
      appointments: true,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "photographer",
        label: "Fotógrafo(a)",
        desc: "Sesiones fotográficas",
      },
      { code: "editor", label: "Editor(a)", desc: "Edición y retoque" },
      { code: "assistant", label: "Asistente", desc: "Asistencia en sesiones" },
    ],
    sidebar: [
      {
        key: "sessions",
        title: "Sesiones",
        icon: "Camera",
        defaultOpen: true,
        items: [
          { path: "/hub/photo-studio", label: "Sesiones", icon: "Camera" },
          {
            path: "/hub/photo-gallery",
            label: "Galería Clientes",
            icon: "Image",
          },
          { path: "/hub/reservations", label: "Agenda", icon: "Calendar" },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Equipos",
        order: "Sesión",
        orderPlural: "Sesiones",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Sesión de Fotos",
          "Video",
          "Edición",
          "Impresión",
          "Álbum",
          "Passport/ID",
          "Evento",
          "Producto",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "duration_min",
        label: "Duración (min)",
        type: "number",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "num_photos",
        label: "Fotos",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "includes_prints",
        label: "Incluye Impresiones",
        type: "boolean",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "location",
        label: "Ubicación",
        type: "select",
        options: ["Estudio", "Exterior", "A Domicilio", "Evento"],
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "duration_min", "location"],
    },
  },

  travel_agency: {
    code: "travel_agency",
    name: "Agencia de Viajes",
    icon: "Plane",
    labels: {
      product: "Paquete",
      productPlural: "Paquetes",
      customer: "Viajero",
      customerPlural: "Viajeros",
      inventory: "Catálogo",
      order: "Reserva",
      orderPlural: "Reservas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Destino",
      categoryPlural: "Destinos",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: true,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "agent",
        label: "Agente de Viajes",
        desc: "Ventas y reservaciones",
      },
      {
        code: "coordinator",
        label: "Coordinador(a)",
        desc: "Coordinación de tours",
      },
    ],
    sidebar: [
      {
        key: "bookings",
        title: "Reservaciones",
        icon: "Plane",
        defaultOpen: true,
        items: [
          {
            path: "/hub/reservations",
            label: "Reservaciones",
            icon: "Calendar",
          },
          {
            path: "/sales/transactions",
            label: "Ventas",
            icon: "ClipboardList",
          },
          { path: "/hub/customers", label: "Viajeros", icon: "Users" },
        ],
      },
      salesSection({
        product: "Paquete",
        productPlural: "Paquetes",
        customer: "Viajero",
        customerPlural: "Viajeros",
        inventory: "Catálogo",
        order: "Reserva",
        orderPlural: "Reservas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Destino",
        categoryPlural: "Destinos",
      }),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Paquete",
          "Boleto Aéreo",
          "Hotel",
          "Crucero",
          "Tour",
          "Seguro",
          "Visa/Trámite",
          "Renta Auto",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "destination",
        label: "Destino",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "duration_days",
        label: "Duración (días)",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "num_travelers",
        label: "Viajeros",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "includes_flights",
        label: "Incluye Vuelos",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "departure",
        label: "Fecha Salida",
        type: "date",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: [
        "service_type",
        "destination",
        "duration_days",
        "num_travelers",
      ],
    },
  },

  bookstore: {
    code: "bookstore",
    name: "Librería / Papelería",
    icon: "BookOpen",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "sales", label: "Vendedor(a)", desc: "Atención al público" },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "type",
        label: "Tipo",
        type: "select",
        options: [
          "Libro",
          "Cuaderno",
          "Útil Escolar",
          "Arte",
          "Oficina",
          "Papelería",
          "Juego Educativo",
          "Revista",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "author",
        label: "Autor / Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "isbn",
        label: "ISBN",
        type: "text",
        showInPOS: false,
        sortOrder: 3,
      },
      {
        key: "language",
        label: "Idioma",
        type: "select",
        options: ["Español", "Inglés", "Francés", "Bilingüe", "N/A"],
        showInPOS: false,
        sortOrder: 4,
      },
      {
        key: "age_range",
        label: "Edad",
        type: "select",
        options: ["Infantil", "Juvenil", "Adulto", "Todas"],
        showInPOS: false,
        sortOrder: 5,
      },
      {
        key: "cover",
        label: "Pasta",
        type: "select",
        options: ["Dura", "Blanda", "N/A"],
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["type", "author"],
    },
  },

  florist: {
    code: "florist",
    name: "Floristería",
    icon: "Flower",
    labels: {
      product: "Arreglo",
      productPlural: "Arreglos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Pedido",
      orderPlural: "Pedidos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Ocasión",
      categoryPlural: "Ocasiones",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "florist",
        label: "Florista",
        desc: "Diseño de arreglos florales",
      },
      { code: "delivery", label: "Repartidor", desc: "Entrega de arreglos" },
    ],
    sidebar: [
      salesSection({
        product: "Arreglo",
        productPlural: "Arreglos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Pedido",
        orderPlural: "Pedidos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Ocasión",
        categoryPlural: "Ocasiones",
      }),
      {
        key: "deliveries",
        title: "Entregas",
        icon: "Truck",
        items: [
          { path: "/hub/delivery", label: "Entregas del Día", icon: "Truck" },
          {
            path: "/hub/service-orders",
            label: "Pedidos Personalizados",
            icon: "ClipboardList",
          },
        ],
      },
      inventorySection({ inventory: "Inventario" } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "flower_type",
        label: "Tipo de Flor",
        type: "select",
        options: [
          "Rosa",
          "Lirio",
          "Girasol",
          "Tulipán",
          "Orquídea",
          "Clavel",
          "Mixto",
          "Artificial",
          "Planta",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "arrangement",
        label: "Arreglo",
        type: "select",
        options: [
          "Ramo",
          "Centro de Mesa",
          "Canasta",
          "Bouquet Novia",
          "Corona",
          "Individual",
          "Maceta",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "occasion",
        label: "Ocasión",
        type: "select",
        options: [
          "Cumpleaños",
          "Aniversario",
          "Boda",
          "Funeral",
          "Amor",
          "Amistad",
          "Graduación",
          "Cualquiera",
        ],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "stems",
        label: "Tallos",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "includes_vase",
        label: "Incluye Florero",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["flower_type", "arrangement", "occasion", "stems"],
    },
  },

  music_store: {
    code: "music_store",
    name: "Tienda de Música / Instrumentos",
    icon: "Music",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: true,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: true,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "sales_advisor",
        label: "Asesor(a)",
        desc: "Ventas de instrumentos",
      },
      { code: "tech", label: "Técnico", desc: "Reparación de instrumentos" },
      { code: "teacher", label: "Profesor(a)", desc: "Clases de música" },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "classes",
        title: "Clases",
        icon: "Music",
        items: [
          {
            path: "/hub/reservations",
            label: "Horario Clases",
            icon: "Calendar",
          },
          { path: "/hub/customers", label: "Estudiantes", icon: "Users" },
        ],
      },
      postSaleSection,
      inventorySection({ inventory: "Inventario" } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "type",
        label: "Tipo",
        type: "select",
        options: [
          "Instrumento",
          "Accesorio",
          "Audio",
          "Partitura",
          "Vinilo",
          "CD",
          "Cable",
          "Micrófono",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "instrument",
        label: "Instrumento",
        type: "select",
        options: [
          "Guitarra",
          "Piano",
          "Batería",
          "Bajo",
          "Violín",
          "Saxofón",
          "Flauta",
          "Amplificador",
          "Otro",
        ],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "condition",
        label: "Condición",
        type: "select",
        options: ["Nuevo", "Usado", "Reacondicionado"],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["type", "brand", "instrument", "condition"],
    },
  },

  cleaning: {
    code: "cleaning",
    name: "Servicios de Limpieza",
    icon: "Sparkles",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Insumos",
      order: "Orden",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "cleaner", label: "Limpiador(a)", desc: "Personal de limpieza" },
      {
        code: "supervisor",
        label: "Supervisor(a)",
        desc: "Supervisión de equipos",
      },
      { code: "driver", label: "Conductor", desc: "Transporte de equipo" },
    ],
    sidebar: [
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Insumos",
        order: "Orden",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "scheduling",
        title: "Agenda",
        icon: "Calendar",
        items: [
          {
            path: "/hub/reservations",
            label: "Agenda de Personal",
            icon: "Calendar",
          },
          {
            path: "/hub/service-orders",
            label: "Órdenes de Trabajo",
            icon: "ClipboardList",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Residencial",
          "Comercial",
          "Industrial",
          "Post-Obra",
          "Fumigación",
          "Tapicería",
          "Alfombras",
          "Cristales",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "area_sqm",
        label: "Área (m²)",
        type: "number",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "frequency",
        label: "Frecuencia",
        type: "select",
        options: ["Única vez", "Semanal", "Quincenal", "Mensual"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "includes_products",
        label: "Incluye Productos",
        type: "boolean",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "staff_count",
        label: "Personal",
        type: "number",
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "area_sqm", "frequency"],
    },
  },

  security: {
    code: "security",
    name: "Seguridad / Vigilancia",
    icon: "Shield",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Equipos",
      order: "Contrato",
      orderPlural: "Contratos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: true,
      crm: true,
      promotions: false,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "guard", label: "Guardia", desc: "Vigilancia y seguridad" },
      {
        code: "supervisor",
        label: "Supervisor",
        desc: "Supervisión de guardias",
      },
      {
        code: "installer",
        label: "Instalador",
        desc: "Instalación de equipos",
      },
      { code: "monitor", label: "Monitorista", desc: "Monitoreo de cámaras" },
    ],
    sidebar: [
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Equipos",
        order: "Contrato",
        orderPlural: "Contratos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "operations",
        title: "Operaciones",
        icon: "Shield",
        items: [
          { path: "/hub/service-orders", label: "Contratos", icon: "FileText" },
          {
            path: "/hub/installations",
            label: "Instalaciones",
            icon: "Wrench",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      inventorySection({ inventory: "Equipos" } as any),
      hrSection,
      accountingSection,
      taxSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Guardia",
          "CCTV",
          "Alarmas",
          "Control Acceso",
          "Escolta",
          "Ronda",
          "Monitoreo 24/7",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "duration",
        label: "Duración",
        type: "select",
        options: ["Evento", "8 Horas", "12 Horas", "24 Horas", "Mensual"],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "guards_count",
        label: "Guardias",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "armed",
        label: "Armado",
        type: "boolean",
        showInPOS: true,
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "duration", "guards_count"],
    },
  },

  daycare: {
    code: "daycare",
    name: "Guardería / Daycare",
    icon: "Baby",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Familia",
      customerPlural: "Familias",
      inventory: "Materiales",
      order: "Matrícula",
      orderPlural: "Matrículas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Grupo",
      categoryPlural: "Grupos",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: true,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "teacher", label: "Maestro(a)", desc: "Educación infantil" },
      { code: "assistant", label: "Auxiliar", desc: "Asistencia en aula" },
      { code: "cook", label: "Cocinero(a)", desc: "Preparación de comidas" },
      { code: "driver", label: "Conductor", desc: "Transporte escolar" },
    ],
    sidebar: [
      {
        key: "children",
        title: "Niños",
        icon: "Baby",
        defaultOpen: true,
        items: [
          { path: "/hub/customers", label: "Niños Inscritos", icon: "Users" },
          {
            path: "/hub/reservations",
            label: "Asistencia Diaria",
            icon: "Calendar",
          },
          {
            path: "/hub/service-orders",
            label: "Matrículas",
            icon: "ClipboardList",
          },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Familia",
        customerPlural: "Familias",
        inventory: "Materiales",
        order: "Matrícula",
        orderPlural: "Matrículas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Grupo",
        categoryPlural: "Grupos",
      }),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "age_group",
        label: "Grupo de Edad",
        type: "select",
        options: [
          "0-1 año",
          "1-2 años",
          "2-3 años",
          "3-5 años",
          "5-12 años",
          "After School",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "schedule",
        label: "Horario",
        type: "select",
        options: ["Medio Día", "Día Completo", "Extendido 7pm", "Por Hora"],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "includes_meals",
        label: "Incluye Comidas",
        type: "boolean",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "includes_transport",
        label: "Incluye Transporte",
        type: "boolean",
        showInPOS: true,
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["age_group", "schedule", "includes_meals"],
    },
  },

  funeral: {
    code: "funeral",
    name: "Funeraria",
    icon: "Cross",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Familia",
      customerPlural: "Familias",
      inventory: "Inventario",
      order: "Servicio",
      orderPlural: "Servicios",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "director",
        label: "Director(a)",
        desc: "Dirección de servicios",
      },
      {
        code: "embalmer",
        label: "Embalsamador(a)",
        desc: "Preparación del difunto",
      },
      {
        code: "coordinator",
        label: "Coordinador(a)",
        desc: "Coordinación de ceremonias",
      },
      { code: "driver", label: "Chofer", desc: "Conducción de carroza" },
    ],
    sidebar: [
      {
        key: "services",
        title: "Servicios",
        icon: "FileText",
        defaultOpen: true,
        items: [
          {
            path: "/hub/service-orders",
            label: "Servicios",
            icon: "ClipboardList",
          },
          {
            path: "/hub/reservations",
            label: "Capillas Reservadas",
            icon: "Calendar",
          },
          { path: "/hub/customers", label: "Familias", icon: "Users" },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Familia",
        customerPlural: "Familias",
        inventory: "Inventario",
        order: "Servicio",
        orderPlural: "Servicios",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Funeral Tradicional",
          "Cremación",
          "Velación",
          "Traslado",
          "Capilla",
          "Flores",
          "Ataúd",
          "Urna",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "coffin_material",
        label: "Material Ataúd",
        type: "select",
        options: ["Madera", "Metal", "MDF", "Económico", "Premium"],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "urn_material",
        label: "Material Urna",
        type: "select",
        options: ["Madera", "Metal", "Cerámica", "Mármol", "Bambú", "N/A"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "duration_hrs",
        label: "Duración Velatorio",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "includes_ceremony",
        label: "Incluye Ceremonia",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: false,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "coffin_material", "duration_hrs"],
    },
  },

  tailor: {
    code: "tailor",
    name: "Sastrería / Costura",
    icon: "Scissors",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Telas e Insumos",
      order: "Orden",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "tailor", label: "Sastre", desc: "Confección y arreglos" },
      { code: "seamstress", label: "Costurero(a)", desc: "Costura general" },
    ],
    sidebar: [
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Telas e Insumos",
        order: "Orden",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "orders",
        title: "Órdenes",
        icon: "ClipboardList",
        items: [
          {
            path: "/hub/service-orders",
            label: "Órdenes de Trabajo",
            icon: "ClipboardList",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      inventorySection({ inventory: "Telas e Insumos" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Confección",
          "Arreglo/Ajuste",
          "Reparación",
          "Bordado",
          "Uniformes",
          "Traje a Medida",
          "Vestido Novia",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "garment",
        label: "Prenda",
        type: "select",
        options: [
          "Camisa",
          "Pantalón",
          "Vestido",
          "Traje",
          "Falda",
          "Chaqueta",
          "Saco",
          "Otra",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "fabric",
        label: "Tipo de Tela",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "complexity",
        label: "Complejidad",
        type: "select",
        options: ["Sencillo", "Intermedio", "Complejo", "Alta Costura"],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "delivery_days",
        label: "Entrega (días)",
        type: "number",
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "garment", "complexity", "delivery_days"],
    },
  },

  tattoo: {
    code: "tattoo",
    name: "Estudio de Tatuaje / Piercing",
    icon: "Pen",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Insumos",
      order: "Cita",
      orderPlural: "Citas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Estilo",
      categoryPlural: "Estilos",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: true,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: true,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "artist", label: "Artista", desc: "Tatuador(a) profesional" },
      { code: "piercer", label: "Piercer", desc: "Perforaciones corporales" },
      { code: "receptionist", label: "Recepcionista", desc: "Citas y caja" },
    ],
    sidebar: [
      {
        key: "appointments",
        title: "Agenda",
        icon: "Calendar",
        defaultOpen: true,
        items: [
          {
            path: "/hub/reservations",
            label: "Agenda de Citas",
            icon: "Calendar",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
          {
            path: "/hub/service-orders",
            label: "Diseños",
            icon: "ClipboardList",
          },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Insumos",
        order: "Cita",
        orderPlural: "Citas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Estilo",
        categoryPlural: "Estilos",
      }),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Tatuaje",
          "Piercing",
          "Micropigmentación",
          "Cover Up",
          "Retoque",
          "Remoción",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "style",
        label: "Estilo",
        type: "select",
        options: [
          "Blackwork",
          "Realismo",
          "Old School",
          "Japonés",
          "Tribal",
          "Minimalista",
          "Acuarela",
          "Geométrico",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "size_cm",
        label: "Tamaño",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "body_part",
        label: "Zona",
        type: "text",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "color_type",
        label: "Color",
        type: "select",
        options: ["B/N", "Color", "Ambos"],
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "sessions",
        label: "Sesiones",
        type: "number",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: false,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: true,
      customFields: ["service_type", "style", "size_cm", "body_part"],
    },
  },

  shipping: {
    code: "shipping",
    name: "Mensajería / Courier",
    icon: "Package",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Insumos",
      order: "Envío",
      orderPlural: "Envíos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: true,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "driver", label: "Conductor", desc: "Reparto y recolección" },
      { code: "dispatcher", label: "Despachador", desc: "Asignación de rutas" },
      { code: "warehouse", label: "Almacenero", desc: "Recepción y despacho" },
    ],
    sidebar: [
      {
        key: "shipping",
        title: "Envíos",
        icon: "Package",
        defaultOpen: true,
        items: [
          { path: "/hub/delivery", label: "Envíos del Día", icon: "Truck" },
          {
            path: "/hub/service-orders",
            label: "Todos los Envíos",
            icon: "ClipboardList",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Insumos",
        order: "Envío",
        orderPlural: "Envíos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      accountingSection,
      taxSection,
      hrSection,
      biSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Mismo Día",
          "Next Day",
          "Estándar 3-5d",
          "Express",
          "Internacional",
          "Marítimo",
          "Aéreo",
          "Terrestre",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "weight_kg",
        label: "Peso (kg)",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "dimensions",
        label: "Dimensiones",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "origin",
        label: "Origen",
        type: "text",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "destination",
        label: "Destino",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "insurance",
        label: "Seguro",
        type: "boolean",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "weight_kg", "origin", "destination"],
    },
  },

  event_venue: {
    code: "event_venue",
    name: "Salón de Eventos",
    icon: "Calendar",
    labels: {
      product: "Espacio",
      productPlural: "Espacios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Equipos",
      order: "Reserva",
      orderPlural: "Reservas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Tipo",
      categoryPlural: "Tipos",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: true,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: true,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: true,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "coordinator",
        label: "Coordinador(a)",
        desc: "Coordinación de eventos",
      },
      { code: "setup", label: "Montajista", desc: "Montaje y desmontaje" },
      { code: "sound", label: "Sonidista", desc: "Equipo de sonido" },
      { code: "bartender", label: "Bartender", desc: "Servicio de barra" },
    ],
    sidebar: [
      {
        key: "events",
        title: "Eventos",
        icon: "Calendar",
        defaultOpen: true,
        items: [
          { path: "/hub/reservations", label: "Calendario", icon: "Calendar" },
          {
            path: "/hub/service-orders",
            label: "Cotizaciones",
            icon: "ClipboardList",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      salesSection({
        product: "Espacio",
        productPlural: "Espacios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Equipos",
        order: "Reserva",
        orderPlural: "Reservas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Tipo",
        categoryPlural: "Tipos",
      }),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "event_type",
        label: "Tipo Evento",
        type: "select",
        options: [
          "Boda",
          "Quinceañero",
          "Cumpleaños",
          "Corporativo",
          "Bautizo",
          "Baby Shower",
          "Graduación",
          "Conferencia",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "capacity",
        label: "Capacidad",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "duration_hrs",
        label: "Duración (hrs)",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "includes_tables",
        label: "Incluye Mesas/Sillas",
        type: "boolean",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "includes_sound",
        label: "Incluye Sonido",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "includes_catering",
        label: "Incluye Catering",
        type: "boolean",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["event_type", "capacity", "duration_hrs"],
    },
  },

  laundry: {
    code: "laundry",
    name: "Lavandería / Dry Cleaning",
    icon: "WashingMachine",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Insumos",
      order: "Orden",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: false,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "washer", label: "Lavador(a)", desc: "Lavado y planchado" },
      { code: "driver", label: "Repartidor", desc: "Recolección y entrega" },
    ],
    sidebar: [
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Insumos",
        order: "Orden",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "queue",
        title: "Cola de Trabajo",
        icon: "Clock",
        items: [
          {
            path: "/hub/service-orders",
            label: "Órdenes Pendientes",
            icon: "ClipboardList",
          },
          { path: "/hub/delivery", label: "Repartos", icon: "Truck" },
        ],
      },
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Lavado",
          "Planchado",
          "Lavado+Planchado",
          "Dry Cleaning",
          "Edredones",
          "Cortinas",
          "Zapatos",
          "Cuero",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "garment",
        label: "Prenda",
        type: "select",
        options: [
          "Camisa",
          "Pantalón",
          "Vestido",
          "Traje",
          "Saco",
          "Chaqueta",
          "Edredón",
          "Sábana",
          "Otra",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "weight_kg",
        label: "Peso (kg)",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "urgency",
        label: "Urgencia",
        type: "select",
        options: ["Normal 48h", "Express 24h", "Mismo Día"],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "stain_treatment",
        label: "Tratamiento Manchas",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "garment", "weight_kg", "urgency"],
    },
  },

  education: {
    code: "education",
    name: "Centro Educativo",
    icon: "GraduationCap",
    labels: {
      product: "Curso",
      productPlural: "Cursos",
      customer: "Estudiante",
      customerPlural: "Estudiantes",
      inventory: "Materiales",
      order: "Matrícula",
      orderPlural: "Matrículas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Área",
      categoryPlural: "Áreas",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: true,
      tableManagement: false,
      loyalty: false,
      ecommerce: true,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "teacher", label: "Profesor(a)", desc: "Docente de cursos" },
      {
        code: "coordinator",
        label: "Coordinador(a)",
        desc: "Coordinación académica",
      },
      { code: "tutor", label: "Tutor(a)", desc: "Clases particulares" },
    ],
    sidebar: [
      {
        key: "academics",
        title: "Académico",
        icon: "GraduationCap",
        defaultOpen: true,
        items: [
          { path: "/hub/education", label: "Estudiantes", icon: "Users" },
          { path: "/hub/reservations", label: "Horarios", icon: "Calendar" },
          {
            path: "/hub/service-orders",
            label: "Matrículas",
            icon: "ClipboardList",
          },
        ],
      },
      salesSection({
        product: "Curso",
        productPlural: "Cursos",
        customer: "Estudiante",
        customerPlural: "Estudiantes",
        inventory: "Materiales",
        order: "Matrícula",
        orderPlural: "Matrículas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Área",
        categoryPlural: "Áreas",
      }),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "subject",
        label: "Materia",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "level",
        label: "Nivel",
        type: "select",
        options: [
          "Preescolar",
          "Primaria",
          "Secundaria",
          "Universidad",
          "Profesional",
          "Todas",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "modality",
        label: "Modalidad",
        type: "select",
        options: ["Presencial", "Virtual", "Híbrido"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "duration_hrs",
        label: "Duración (hrs)",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "certificate",
        label: "Incluye Certificado",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "group_size",
        label: "Tamaño Grupo",
        type: "select",
        options: ["Individual", "2-5", "6-15", "16-30", "30+"],
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["subject", "level", "modality", "duration_hrs"],
    },
  },

  legal: {
    code: "legal",
    name: "Oficina Legal / Notaría",
    icon: "Scale",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Documentos",
      order: "Caso",
      orderPlural: "Casos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Área",
      categoryPlural: "Áreas",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "lawyer",
        label: "Abogado(a)",
        desc: "Asesoría y representación legal",
      },
      { code: "paralegal", label: "Asistente Legal", desc: "Apoyo en casos" },
      { code: "notary", label: "Notario(a)", desc: "Servicios notariales" },
    ],
    sidebar: [
      {
        key: "cases",
        title: "Casos",
        icon: "Scale",
        defaultOpen: true,
        items: [
          {
            path: "/hub/service-orders",
            label: "Casos",
            icon: "ClipboardList",
          },
          { path: "/hub/reservations", label: "Agenda", icon: "Calendar" },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Documentos",
        order: "Caso",
        orderPlural: "Casos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Área",
        categoryPlural: "Áreas",
      }),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Consulta",
          "Contrato",
          "Poder Notarial",
          "Escritura",
          "Testamento",
          "Demanda",
          "Asesoría",
          "Registro",
          "Apostilla",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "area",
        label: "Área Legal",
        type: "select",
        options: [
          "Civil",
          "Penal",
          "Laboral",
          "Mercantil",
          "Familiar",
          "Inmobiliario",
          "Tributario",
          "Migratorio",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "duration_min",
        label: "Duración (min)",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "notarized",
        label: "Notariado",
        type: "boolean",
        showInPOS: true,
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "area", "duration_min"],
    },
  },

  accounting_firm: {
    code: "accounting_firm",
    name: "Contabilidad / Auditoría",
    icon: "Calculator",
    labels: {
      product: "Servicio",
      productPlural: "Servicios",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Documentos",
      order: "Contrato",
      orderPlural: "Contratos",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: true,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "accountant",
        label: "Contador(a)",
        desc: "Contabilidad y auditoría",
      },
      {
        code: "tax_specialist",
        label: "Especialista Fiscal",
        desc: "Impuestos y DGII",
      },
      {
        code: "auditor",
        label: "Auditor(a)",
        desc: "Auditoría interna/externa",
      },
    ],
    sidebar: [
      {
        key: "clients",
        title: "Clientes",
        icon: "Users",
        defaultOpen: true,
        items: [
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
          {
            path: "/hub/service-orders",
            label: "Contratos de Servicio",
            icon: "ClipboardList",
          },
          { path: "/hub/reservations", label: "Agenda", icon: "Calendar" },
        ],
      },
      salesSection({
        product: "Servicio",
        productPlural: "Servicios",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Documentos",
        order: "Contrato",
        orderPlural: "Contratos",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "service_type",
        label: "Tipo",
        type: "select",
        options: [
          "Declaración ISR",
          "Contabilidad Mensual",
          "Auditoría",
          "Nómina",
          "Constitución",
          "Consultoría",
          "Estados Financieros",
          "Liquidación",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "period",
        label: "Período",
        type: "select",
        options: ["Mensual", "Trimestral", "Anual", "Única vez"],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "company_size",
        label: "Tamaño Empresa",
        type: "select",
        options: ["Micro", "Pequeña", "Mediana", "Grande"],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "includes_filing",
        label: "Radicación DGII",
        type: "boolean",
        showInPOS: true,
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["service_type", "period", "company_size"],
    },
  },

  real_estate: {
    code: "real_estate",
    name: "Inmobiliaria",
    icon: "Home",
    labels: {
      product: "Propiedad",
      productPlural: "Propiedades",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Catálogo",
      order: "Operación",
      orderPlural: "Operaciones",
      supplier: "Propietario",
      supplierPlural: "Propietarios",
      category: "Tipo",
      categoryPlural: "Tipos",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: false,
      reservations: false,
      appointments: true,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: true,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: true,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "agent",
        label: "Agente Inmobiliario",
        desc: "Ventas y alquileres",
      },
      {
        code: "appraiser",
        label: "Avaluador",
        desc: "Valuación de propiedades",
      },
      {
        code: "property_mgr",
        label: "Administrador",
        desc: "Administración de propiedades",
      },
    ],
    sidebar: [
      {
        key: "properties",
        title: "Propiedades",
        icon: "Home",
        defaultOpen: true,
        items: [
          { path: "/hub/realestate", label: "Catálogo", icon: "Home" },
          {
            path: "/hub/service-orders",
            label: "Operaciones",
            icon: "ClipboardList",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
          { path: "/hub/reservations", label: "Visitas", icon: "Calendar" },
        ],
      },
      salesSection({
        product: "Propiedad",
        productPlural: "Propiedades",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Catálogo",
        order: "Operación",
        orderPlural: "Operaciones",
        supplier: "Propietario",
        supplierPlural: "Propietarios",
        category: "Tipo",
        categoryPlural: "Tipos",
      }),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "listing_type",
        label: "Operación",
        type: "select",
        options: ["Venta", "Alquiler", "Alquiler Vacacional", "Anticrético"],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "property_type",
        label: "Tipo",
        type: "select",
        options: [
          "Apartamento",
          "Casa",
          "Local Comercial",
          "Oficina",
          "Solar",
          "Villa",
          "Penthouse",
          "Nave Industrial",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "bedrooms",
        label: "Habitaciones",
        type: "number",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "bathrooms",
        label: "Baños",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "area_sqm",
        label: "Área (m²)",
        type: "number",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "parking",
        label: "Parqueos",
        type: "number",
        showInPOS: true,
        sortOrder: 6,
      },
      {
        key: "location",
        label: "Ubicación",
        type: "text",
        required: true,
        showInPOS: true,
        sortOrder: 7,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: [
        "listing_type",
        "property_type",
        "bedrooms",
        "area_sqm",
        "location",
      ],
    },
  },

  printing: {
    code: "printing",
    name: "Imprenta / Print Shop",
    icon: "Printer",
    labels: {
      product: "Trabajo",
      productPlural: "Trabajos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Insumos",
      order: "Orden",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "operator", label: "Operador(a)", desc: "Operación de máquinas" },
      { code: "designer", label: "Diseñador(a)", desc: "Diseño gráfico" },
      {
        code: "finishing",
        label: "Acabados",
        desc: "Corte, doblado, encuadernación",
      },
    ],
    sidebar: [
      salesSection({
        product: "Trabajo",
        productPlural: "Trabajos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Insumos",
        order: "Orden",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      {
        key: "jobs",
        title: "Trabajos",
        icon: "Printer",
        items: [
          {
            path: "/hub/service-orders",
            label: "Órdenes de Trabajo",
            icon: "ClipboardList",
          },
          { path: "/hub/customers", label: "Clientes", icon: "Users" },
        ],
      },
      inventorySection({ inventory: "Insumos" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "product_type",
        label: "Producto",
        type: "select",
        options: [
          "Tarjetas",
          "Flyers",
          "Brochures",
          "Banners",
          "Camisetas",
          "Tazas",
          "Stickers",
          "Lonas",
          "Menú",
          "Invitaciones",
          "Libros",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "size",
        label: "Tamaño",
        type: "select",
        options: [
          "A4",
          "A3",
          "Carta",
          "Oficio",
          "2x3m",
          "3x5m",
          "Personalizado",
        ],
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "paper",
        label: "Papel/Sustrato",
        type: "select",
        options: [
          "Bond",
          "Couche",
          "Opalina",
          "Adhesivo",
          "Vinil",
          "Lona",
          "Tela",
          "Madera",
        ],
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "color_mode",
        label: "Color",
        type: "select",
        options: ["Full Color", "B/N", "1 Tinta", "2 Tintas"],
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "quantity",
        label: "Cantidad",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "finish",
        label: "Acabado",
        type: "select",
        options: ["Brillante", "Mate", "Satinado", "Laminado", "Sin acabado"],
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: false,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["product_type", "size", "paper", "quantity"],
    },
  },

  agriculture: {
    code: "agriculture",
    name: "Agricultura / Agropecuaria",
    icon: "Wheat",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: true,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: false,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: true,
      expirationTracking: true,
      recipeManagement: false,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      { code: "farmer", label: "Agricultor(a)", desc: "Trabajo de campo" },
      { code: "foreman", label: "Capataz", desc: "Supervisión de finca" },
      { code: "agronomist", label: "Agrónomo(a)", desc: "Asesoría técnica" },
    ],
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
    ],
    productFields: [
      {
        key: "product_type",
        label: "Tipo",
        type: "select",
        options: [
          "Fruta",
          "Verdura",
          "Grano",
          "Legumbre",
          "Hierba",
          "Ganadería",
          "Avicultura",
          "Floricultura",
          "Orgánico",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "variety",
        label: "Variedad",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "weight_kg",
        label: "Peso (kg)",
        type: "number",
        required: true,
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "origin",
        label: "Finca",
        type: "text",
        showInPOS: false,
        sortOrder: 4,
      },
      {
        key: "is_organic",
        label: "Orgánico",
        type: "boolean",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "harvest_season",
        label: "Temporada",
        type: "select",
        options: ["Todo el año", "Primavera", "Verano", "Otoño", "Invierno"],
        showInPOS: false,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "counter",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["product_type", "variety", "weight_kg", "is_organic"],
    },
  },

  factory: {
    code: "factory",
    name: "Fábrica / Manufactura",
    icon: "Factory",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Orden de Producción",
      orderPlural: "Órdenes",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Línea",
      categoryPlural: "Líneas",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: true,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: false,
      ecommerce: false,
      serviceOrders: true,
      warranties: false,
      installations: false,
      crm: true,
      promotions: false,
      commissions: false,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: true,
      lotTracking: true,
      expirationTracking: false,
      recipeManagement: true,
      weightPricing: true,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: [
      ...commonEmployees,
      {
        code: "operator",
        label: "Operador(a)",
        desc: "Operación de maquinaria",
      },
      {
        code: "supervisor",
        label: "Supervisor(a)",
        desc: "Supervisión de línea",
      },
      {
        code: "qc",
        label: "Control de Calidad",
        desc: "Inspección de productos",
      },
      {
        code: "maintenance",
        label: "Mantenimiento",
        desc: "Mantenimiento de equipos",
      },
      {
        code: "warehouse",
        label: "Almacenista",
        desc: "Materia prima y producto terminado",
      },
    ],
    sidebar: [
      {
        key: "production",
        title: "Producción",
        icon: "Factory",
        defaultOpen: true,
        items: [
          {
            path: "/hub/service-orders",
            label: "Órdenes de Producción",
            icon: "ClipboardList",
          },
          {
            path: "/hub/installations",
            label: "Líneas de Producción",
            icon: "Wrench",
          },
        ],
      },
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Orden de Producción",
        orderPlural: "Órdenes",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Línea",
        categoryPlural: "Líneas",
      }),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      biSection,
    ],
    productFields: [
      {
        key: "category",
        label: "Categoría",
        type: "select",
        options: [
          "Alimentos",
          "Bebidas",
          "Textiles",
          "Plásticos",
          "Metal",
          "Madera",
          "Químicos",
          "Otro",
        ],
        required: true,
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "material",
        label: "Materia Prima",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "dimensions",
        label: "Dimensiones",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "weight_kg",
        label: "Peso (kg)",
        type: "number",
        showInPOS: true,
        sortOrder: 4,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        sortOrder: 5,
      },
      {
        key: "min_order",
        label: "Pedido Mínimo",
        type: "number",
        showInPOS: true,
        sortOrder: 6,
      },
    ],
    pos: {
      layout: "list",
      showCategories: true,
      showSearch: true,
      showImages: false,
      showPrice: true,
      showStock: true,
      quickPay: false,
      splitBill: false,
      tipPrompt: false,
      customFields: ["category", "material", "dimensions", "weight_kg"],
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // GENERIC / FALLBACK
  // ═══════════════════════════════════════════════════════════════

  general: {
    code: "general",
    name: "Negocio General",
    icon: "Store",
    labels: {
      product: "Producto",
      productPlural: "Productos",
      customer: "Cliente",
      customerPlural: "Clientes",
      inventory: "Inventario",
      order: "Venta",
      orderPlural: "Ventas",
      supplier: "Proveedor",
      supplierPlural: "Proveedores",
      category: "Categoría",
      categoryPlural: "Categorías",
    },
    modules: {
      pos: true,
      kds: false,
      delivery: true,
      reservations: false,
      appointments: false,
      prescriptions: false,
      vehicleIntake: false,
      roomManagement: false,
      patientRecords: false,
      projectManagement: false,
      fleetManagement: false,
      membership: false,
      classSchedule: false,
      tableManagement: false,
      loyalty: true,
      ecommerce: true,
      serviceOrders: true,
      warranties: true,
      installations: false,
      crm: true,
      promotions: true,
      commissions: true,
      multiBranch: true,
      multiCurrency: false,
      serialNumbers: false,
      lotTracking: false,
      expirationTracking: false,
      recipeManagement: false,
      weightPricing: false,
      tipManagement: false,
      splitBill: false,
    },
    employeeTypes: commonEmployees,
    sidebar: [
      salesSection({
        product: "Producto",
        productPlural: "Productos",
        customer: "Cliente",
        customerPlural: "Clientes",
        inventory: "Inventario",
        order: "Venta",
        orderPlural: "Ventas",
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
        category: "Categoría",
        categoryPlural: "Categorías",
      }),
      customerSection({
        customer: "Cliente",
        customerPlural: "Clientes",
      } as any),
      inventorySection({ inventory: "Inventario" } as any),
      purchasingSection({
        supplier: "Proveedor",
        supplierPlural: "Proveedores",
      } as any),
      accountingSection,
      taxSection,
      hrSection,
      operationsSection,
      postSaleSection,
      biSection,
      ecommerceSection,
    ],
    productFields: [
      {
        key: "brand",
        label: "Marca",
        type: "text",
        showInPOS: true,
        sortOrder: 1,
      },
      {
        key: "model",
        label: "Modelo",
        type: "text",
        showInPOS: true,
        sortOrder: 2,
      },
      {
        key: "color",
        label: "Color",
        type: "text",
        showInPOS: true,
        sortOrder: 3,
      },
      {
        key: "size",
        label: "Talla/Tamaño",
        type: "text",
        showInPOS: true,
        sortOrder: 4,
      },
    ],
    pos: {
      layout: "grid",
      showCategories: true,
      showSearch: true,
      showImages: true,
      showPrice: true,
      showStock: true,
      quickPay: true,
      splitBill: false,
      tipPrompt: false,
      customFields: ["brand", "model", "color", "size"],
    },
  },
};

// ── Helper: Get config with fallback ───────────────────────────────────

const SPANISH_TO_ENGLISH_MAP: Record<string, string> = {
  restaurante: "restaurant",
  ferreteria: "hardware_store",
  hotel: "hotel",
  gimnasio: "gym",
  carniceria: "butcher",
  meatshop: "butcher",
  general: "general",
};

export function getBusinessConfig(
  code: string | null | undefined,
): BusinessTypeConfig {
  const translatedCode = code ? (SPANISH_TO_ENGLISH_MAP[code.toLowerCase().trim()] || code) : code;
  if (!translatedCode || !BUSINESS_TYPE_CONFIGS[translatedCode]) {
    return BUSINESS_TYPE_CONFIGS["general"];
  }
  return BUSINESS_TYPE_CONFIGS[translatedCode];
}

// ── Default labels for all business types not yet fully configured ─────

const defaultLabels: BusinessLabels = {
  product: "Producto",
  productPlural: "Productos",
  customer: "Cliente",
  customerPlural: "Clientes",
  inventory: "Inventario",
  order: "Venta",
  orderPlural: "Ventas",
  supplier: "Proveedor",
  supplierPlural: "Proveedores",
  category: "Categoría",
  categoryPlural: "Categorías",
};

const defaultModules: BusinessModules = {
  pos: true,
  kds: false,
  delivery: true,
  reservations: false,
  appointments: false,
  prescriptions: false,
  vehicleIntake: false,
  roomManagement: false,
  patientRecords: false,
  projectManagement: false,
  fleetManagement: false,
  membership: false,
  classSchedule: false,
  tableManagement: false,
  loyalty: true,
  ecommerce: true,
  serviceOrders: true,
  warranties: true,
  installations: false,
  crm: true,
  promotions: true,
  commissions: true,
  multiBranch: true,
  multiCurrency: false,
  serialNumbers: false,
  lotTracking: false,
  expirationTracking: false,
  recipeManagement: false,
  weightPricing: false,
  tipManagement: false,
  splitBill: false,
};

// Business types that don't have full configs yet use these defaults
// They still get adapted by their product attributes from the DB
export function getBusinessConfigSafe(
  code: string | null | undefined,
): BusinessTypeConfig {
  const translatedCode = code ? (SPANISH_TO_ENGLISH_MAP[code.toLowerCase().trim()] || code) : code;
  if (translatedCode && BUSINESS_TYPE_CONFIGS[translatedCode]) {
    return BUSINESS_TYPE_CONFIGS[translatedCode];
  }
  // Return generic config for unconfigured types
  return {
    code: translatedCode || "general",
    name: translatedCode || "Negocio General",
    icon: "Store",
    labels: { ...defaultLabels },
    modules: { ...defaultModules },
    employeeTypes: commonEmployees,
    sidebar: BUSINESS_TYPE_CONFIGS["general"].sidebar,
    productFields: [],
    pos: BUSINESS_TYPE_CONFIGS["general"].pos,
  };
}

